import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Nav,
  Tab,
  Button,
  Form,
  Row,
  Col,
  Table,
} from 'react-bootstrap';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  query,
  where,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './VisitModal.module.css';
import { toast, Toaster } from 'react-hot-toast';
import ExpenseModal from './ExpenseModal';
import ShipmentTracking from './ShipmentTracking';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  fetchAccessiblePatients,
  fetchVisitedPatientIds,
  normalizePatientId,
} from '../../utils/patientAccess';
import {
  fetchLastVisitLocation,
  getEmployeeProjectCenterIds,
} from '../../utils/visitDefaults';
import { deleteVisitMirrors, isMirrorVisit, syncVisitMirrors } from '../../utils/hybridVisitMirror';
import {
  getMissingCompletionKeys,
  getPendingCompletionLabels,
} from '../../utils/visitCompletion';
import {
  dedupeCenters,
  optionMatchesAnyCenterId,
  resolveCanonicalCenterId,
} from '../../utils/centerDedupe';

// CRA Onay sekmesi kaldırıldı. Kod ve gönderim akışı yerinde bırakıldı; geri
// açmak için bu sabiti true yapmak yeterli. Sekmeyle birlikte, harcaması olan
// ziyaretlerde CRA seçimini zorunlu kılan kayıt kontrolü de kaldırıldı
// (aşağıda handleSave içinde), çünkü sekme olmadan CRA seçilemezdi.
const SHOW_CRA_APPROVAL_TAB = false;

// Create a styled container component to apply our styles
const StyledModalContainer = ({ children }) => {
  return <div className={styles.visitModalWrapper}>{children}</div>;
};

const VisitModal = ({ show, onHide, visit, onVisitUpdate, onCascadeDelete }) => {
  const [activeTab, setActiveTab] = useState('informations');
  const [hospitals, setHospitals] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cras, setCras] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [projectTitle, setProjectTitle] = useState('');
  const [projectPhase, setProjectPhase] = useState('');
  const [projectCentersType, setProjectCentersType] = useState('');
  // Centers the logged in user is assigned to on this visit's project, used to
  // pre-fill and shortlist the Hastane / Şehir fields.
  const [projectCenterIds, setProjectCenterIds] = useState([]);
  const [locationAutoFilledFrom, setLocationAutoFilledFrom] = useState('');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [isPastVisit, setIsPastVisit] = useState(false);
  const [isSendingCraApproval, setIsSendingCraApproval] = useState(false);
  const [craApprovalSent, setCraApprovalSent] = useState(false);
  const [craApprovalSentAt, setCraApprovalSentAt] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  // Kargo takip kayıtları (Visits/{id}.shipments)
  const [shipments, setShipments] = useState([]);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [projectPatients, setProjectPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // Get user authorization from context
  const { userAuthorization, employeeData, userEmail, employeeId } = useUserProfile();

  // Check if user is admin (either full admin or restricted admin)
  const isAdmin =
    userAuthorization.includes('admin') ||
    userAuthorization.includes('Kısıtlı Admin');

  // Check if user is a restricted user (can only view and delete)
  const isRestrictedUser = userEmail === 'turanemre1999@gmail.com' && !isAdmin;

  // Check if user owns this visit (created by themselves from personal calendar)
  const isVisitOwner = visit?.employeeId === employeeId;

  // Determine if the modal should be read-only (past visit AND not admin, OR restricted user)
  const isReadOnly = (isPastVisit && !isAdmin) || isRestrictedUser;

  // Initial form state
  const initialFormState = {
    visitType: '',
    sourceDocument: '',
    sourceDocumentCompletionTime: '', // Kaynak Döküman Tamamlanma Saati
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    patientId: '',
    visitNumber: '',
    doctor: '',
    hospital: '',
    city: '',
    travelType: '',
    information: '',
    selectedCra: '',
    expenses: [], // Include expenses in initial form state
    // Home visit top-level fields (matching ManualDataEntryModal)
    visitStartDate: '',
    visitStartTime: '',
    visitEndDate: '',
    visitEndTime: '',
    clinicianFullName: '',
    visitEvaluation: {
      // Travel (Home visit only)
      departureTime: '',
      arrivalAtPatientTime: '',
      departureFromPatientTime: '',
      arrivalHomeTime: '',
      travelTimeBefore: '',
      travelDistanceBefore: '',
      travelTimeAfter: '',
      travelDistanceAfter: '',
      // Travel split fields for UI (hours/minutes)
      travelBeforeHours: '',
      travelBeforeMinutes: '',
      travelAfterHours: '',
      travelAfterMinutes: '',
      shippingDate: '',
      shippingCompany: '',
      shippingTrackingNumber: '',
      // Clinical completion
      ecrfCompleted: '',
      ecrfCompletionDate: '',
      ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
      sourceDocCompleted: '',
      sourceDocCompletionDate: '',
      sourceDocCompletionTime: '', // Yeni Kaynak Dok. tamamlanma saati alanı
      signaturesCompleted: '',
      signatureCompletionDate: '',
      signatureCompletionTime: '', // Yeni İmza tamamlanma saati alanı
      protocolDeviation: '',
      capaOpened: '',
      // Timeline
      processEndDate: '',
      // Issue tracking
      issueOccurred: '',
      issueCategory: '',
      issueTitle: '',
      ivAttemptCount: '',
      issueDetails: '',
      // SLA
      slaTarget: '',
      // Hastane-specific
      screenFail: '',
      dropPatient: '',
      // Home-specific - window & patient
      windowOpenDate: '',
      targetDate: '',
      windowCloseDate: '',
      clinicianFullName: '',
      subjectStatus: '',
      visitEndDate: '',
      // Home-specific - visit detail fields
      visitDurationHours: '',
      visitDurationMinutes: '',
      visitDistance: '',
      issueResultedInMiss: '',
      wasAeSaeTaken: '',
      // Home-specific - correction & CAPA tracking
      smoCorrectionRequests: '',
      qualityManagerCheck: '',
      craRequest: '',
      correctionReason: '',
      dateInitialSourceReceived: '',
      dateLatestSourceRevision: '',
      dateSourceFinalized: '',
      dcfCreated: '',
      isClosed: '',
      capaStartDate: '',
      capaClosedDate: '',
      targetAchieved: '',
      severity: '',
    },
    todoList: {
      pre_patientCalled: false,
      pre_nurseTransferArranged: false,
      pre_courierArranged: false,
      pre_hospitalApproval: false,
      pre_nurseEquipmentReady: false,
      pre_accommodationPlanned: false,
      patientConsent: false,
      protocolUpdate: false,
      kvkkApproval: false,
      visitNumberCheck: false,
      projectCheck: false,
      workflowCheck: false,
      adverseEventCheck: false,
      medicationCheck: false,
      sourceDocuments: false,
      sourceDocumentsApproval: false,
      sourceDocumentsShared: false,
      transferForm: false,
      documentShipping: false,
      // İşaretlendiğinde ziyaret, Stok sekmesine en az bir malzeme girilmeden
      // tamamlanamaz (bkz. handleCompleteVisit).
      stockEntryRequired: false,
    },
  };

  // Form state
  const [formData, setFormData] = useState(initialFormState);

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormState);
    setActiveTab('informations');
    setProjectTitle('');
    setProjectPhase('');
    setProjectCentersType('');
    setProjectCenterIds([]);
    setLocationAutoFilledFrom('');
  };

  // Load existing visit data when modal opens
  useEffect(() => {
    const loadVisitData = async () => {
      if (visit && visit.id) {
        try {
          // Fetch the latest visit data from Firebase to ensure we have current expenses
          const visitRef = doc(db, 'Visits', visit.id);
          const visitDoc = await getDoc(visitRef);

          let currentVisitData = visit;
          if (visitDoc.exists()) {
            currentVisitData = { id: visit.id, ...visitDoc.data() };
          }

          setFormData((prev) => ({
            ...prev,
            ...currentVisitData,
            // If no 'date' field, fall back to firstVisitDate (PersonalCalendar creates visits with firstVisitDate only)
            date: currentVisitData.date || currentVisitData.firstVisitDate || prev.date,
            expenses: currentVisitData.expenses || [], // Include expenses in formData
            // Populate top-level home visit fields
            visitStartDate: currentVisitData.visitStartDate || currentVisitData.date || currentVisitData.firstVisitDate || '',
            visitStartTime: currentVisitData.visitStartTime || currentVisitData.startTime || '',
            visitEndDate: currentVisitData.visitEndDate || '',
            visitEndTime: currentVisitData.visitEndTime || '',
            clinicianFullName: currentVisitData.clinicianFullName || currentVisitData.visitEvaluation?.clinicianFullName || '',
            visitEvaluation: {
              departureTime: '',
              arrivalAtPatientTime: '',
              departureFromPatientTime: '',
              arrivalHomeTime: '',
              travelTimeBefore: '',
              travelDistanceBefore: '',
              travelTimeAfter: '',
              travelDistanceAfter: '',
              travelBeforeHours: '',
              travelBeforeMinutes: '',
              travelAfterHours: '',
              travelAfterMinutes: '',
              shippingDate: '',
              ecrfCompleted: '',
              ecrfCompletionDate: '',
              ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
              sourceDocCompleted: '',
              sourceDocCompletionDate: '',
              signaturesCompleted: '',
              signatureCompletionDate: '',
              protocolDeviation: '',
              capaOpened: '',
              processEndDate: '',
              issueOccurred: '',
              issueCategory: '',
              issueTitle: '',
              ivAttemptCount: '',
              issueDetails: '',
              slaTarget: '',
              screenFail: '',
              dropPatient: '',
              windowOpenDate: '',
              targetDate: '',
              windowCloseDate: '',
              clinicianFullName: '',
              subjectStatus: '',
              visitEndDate: '',
              visitDurationHours: '',
              visitDurationMinutes: '',
              visitDistance: '',
              issueResultedInMiss: '',
              wasAeSaeTaken: '',
              smoCorrectionRequests: '',
              qualityManagerCheck: '',
              craRequest: '',
              correctionReason: '',
              dateInitialSourceReceived: '',
              dateLatestSourceRevision: '',
              dateSourceFinalized: '',
              dcfCreated: '',
              isClosed: '',
              capaStartDate: '',
              capaClosedDate: '',
              targetAchieved: '',
              severity: '',
              ...currentVisitData.visitEvaluation,
              // Compute split travel hours/minutes from total minutes
              travelBeforeHours: currentVisitData.visitEvaluation?.travelTimeBefore
                ? String(Math.floor(Number(currentVisitData.visitEvaluation.travelTimeBefore) / 60))
                : '',
              travelBeforeMinutes: currentVisitData.visitEvaluation?.travelTimeBefore
                ? String(Number(currentVisitData.visitEvaluation.travelTimeBefore) % 60)
                : '',
              travelAfterHours: currentVisitData.visitEvaluation?.travelTimeAfter
                ? String(Math.floor(Number(currentVisitData.visitEvaluation.travelTimeAfter) / 60))
                : '',
              travelAfterMinutes: currentVisitData.visitEvaluation?.travelTimeAfter
                ? String(Number(currentVisitData.visitEvaluation.travelTimeAfter) % 60)
                : '',
            },
            todoList: {
              // Varsayılanlar tek yerden gelir; kayıtta olmayan yeni maddeler
              // böylece false başlar (kontrolsüz checkbox olmaz).
              ...initialFormState.todoList,
              ...currentVisitData.todoList,
            },
          }));

          // Check if the visit period has ended (last day has passed)
          // Allow completion up to 72 hours (3 days) after the visit ends
          if (currentVisitData.firstVisitDate && currentVisitData.period) {
            const firstDate = new Date(currentVisitData.firstVisitDate);
            firstDate.setHours(0, 0, 0, 0);
            
            const period = currentVisitData.period;
            let lastDate;
            
            if (period >= 0) {
              // Positive period: calculate the last day forward from firstDate
              lastDate = new Date(firstDate);
              lastDate.setDate(firstDate.getDate() + period - 1);
            } else {
              // Negative period: the last day is the firstVisitDate itself
              lastDate = new Date(firstDate);
            }
            
            // Set to end of the last day
            lastDate.setHours(23, 59, 59, 999);
            
            // Add 72 hours (3 days) grace period for completion
            const completionDeadline = new Date(lastDate);
            completionDeadline.setHours(completionDeadline.getHours() + 72);
            
            const today = new Date();
            
            // Visit is in the past only if today is AFTER the completion deadline (last day + 72 hours)
            if (today > completionDeadline) {
              setIsPastVisit(true);
            } else {
              setIsPastVisit(false);
            }
          } else {
            setIsPastVisit(false);
          }

          // Set expenses from the latest Firebase data
          if (
            currentVisitData.expenses &&
            Array.isArray(currentVisitData.expenses)
          ) {
            setExpenses(currentVisitData.expenses);
          } else {
            setExpenses([]);
          }

          // CRA approval state from document
          if (
            currentVisitData.craApproval &&
            currentVisitData.craApproval.sent
          ) {
            setCraApprovalSent(true);
            setCraApprovalSentAt(currentVisitData.craApproval.sentAt || null);
          } else {
            setCraApprovalSent(false);
            setCraApprovalSentAt(null);
          }

          // Fetch project data if projectId exists
          if (currentVisitData.projectId) {
            try {
              const projectDoc = await getDoc(
                doc(db, 'Projects', currentVisitData.projectId)
              );
              if (projectDoc.exists()) {
                const projectData = projectDoc.data();
                setProjectTitle(projectData.titleShort || '');
                setProjectPhase(projectData.projectPhase || '');
                setProjectCentersType(projectData.projectCentersType || '');
                setProjectCenterIds(
                  getEmployeeProjectCenterIds(projectData, employeeId)
                );
              }
            } catch (error) {
              console.error('Error fetching project data:', error);
              toast.error('Proje bilgisi yüklenirken bir hata oluştu');
            }
          }
        } catch (error) {
          console.error('Error fetching visit data:', error);
          toast.error('Ziyaret bilgisi yüklenirken bir hata oluştu');

          // Fallback to the provided visit data
          setFormData((prev) => ({
            ...prev,
            ...visit,
            date: visit.date || visit.firstVisitDate || prev.date,
            expenses: visit.expenses || [], // Include expenses in formData
            visitStartDate: visit.visitStartDate || visit.date || visit.firstVisitDate || '',
            visitStartTime: visit.visitStartTime || visit.startTime || '',
            visitEndDate: visit.visitEndDate || '',
            visitEndTime: visit.visitEndTime || '',
            clinicianFullName: visit.clinicianFullName || visit.visitEvaluation?.clinicianFullName || '',
            visitEvaluation: {
              departureTime: '',
              arrivalAtPatientTime: '',
              departureFromPatientTime: '',
              arrivalHomeTime: '',
              travelTimeBefore: '',
              travelDistanceBefore: '',
              travelTimeAfter: '',
              travelDistanceAfter: '',
              travelBeforeHours: '',
              travelBeforeMinutes: '',
              travelAfterHours: '',
              travelAfterMinutes: '',
              shippingDate: '',
              ecrfCompleted: '',
              ecrfCompletionDate: '',
              ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
              sourceDocCompleted: '',
              sourceDocCompletionDate: '',
              signaturesCompleted: '',
              signatureCompletionDate: '',
              protocolDeviation: '',
              capaOpened: '',
              processEndDate: '',
              issueOccurred: '',
              issueCategory: '',
              issueTitle: '',
              ivAttemptCount: '',
              issueDetails: '',
              slaTarget: '',
              ...visit.visitEvaluation,
              // Compute split travel hours/minutes from total minutes
              travelBeforeHours: visit.visitEvaluation?.travelTimeBefore
                ? String(Math.floor(Number(visit.visitEvaluation.travelTimeBefore) / 60))
                : '',
              travelBeforeMinutes: visit.visitEvaluation?.travelTimeBefore
                ? String(Number(visit.visitEvaluation.travelTimeBefore) % 60)
                : '',
              travelAfterHours: visit.visitEvaluation?.travelTimeAfter
                ? String(Math.floor(Number(visit.visitEvaluation.travelTimeAfter) / 60))
                : '',
              travelAfterMinutes: visit.visitEvaluation?.travelTimeAfter
                ? String(Number(visit.visitEvaluation.travelTimeAfter) % 60)
                : '',
            },
            todoList: {
              // Varsayılanlar tek yerden gelir; kayıtta olmayan yeni maddeler
              // böylece false başlar (kontrolsüz checkbox olmaz).
              ...initialFormState.todoList,
              ...visit.todoList,
            },
          }));

          if (visit.expenses) {
            setExpenses(visit.expenses);
          } else {
            setExpenses([]);
          }

          // CRA approval state from provided visit
          if (visit.craApproval && visit.craApproval.sent) {
            setCraApprovalSent(true);
            setCraApprovalSentAt(visit.craApproval.sentAt || null);
          } else {
            setCraApprovalSent(false);
            setCraApprovalSentAt(null);
          }
        }
      } else {
        // If no visit is provided, reset the form
        resetForm();
        setExpenses([]);
      }
    };

    loadVisitData();
  }, [visit]);

  // Fetch the patients the logged in user is allowed to pick: the ones they
  // own, plus the ones they already booked a visit with. Someone else's patient
  // is never offered here, whatever the viewer's role is.
  const fetchProjectPatients = useCallback(
    async (projectId, currentPatientId) => {
      if (!projectId || !employeeId) {
        setProjectPatients([]);
        return;
      }

      setLoadingPatients(true);
      try {
        const visitedPatientIds = await fetchVisitedPatientIds(
          projectId,
          employeeId
        );
        const patientsData = await fetchAccessiblePatients(
          projectId,
          employeeId,
          {
            visitedPatientIds,
            // Never drop the value that is already saved on this visit.
            alwaysIncludeIds: [currentPatientId],
          }
        );
        setProjectPatients(patientsData);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setProjectPatients([]);
      } finally {
        setLoadingPatients(false);
      }
    },
    [employeeId]
  );

  // Fetch patients when visit changes (based on projectId)
  useEffect(() => {
    if (visit?.projectId) {
      fetchProjectPatients(visit.projectId, visit.patientId);
    } else {
      setProjectPatients([]);
    }
  }, [visit?.projectId, visit?.patientId, fetchProjectPatients]);

  // Debug useEffect to monitor expenses changes
  useEffect(() => {
    console.log('Expenses state changed:', expenses);
    console.log('Number of expenses:', expenses.length);
  }, [expenses]);

  // Fetch available stocks from Firestore
  const fetchAvailableStocks = async () => {
    try {
      if (!visit?.employeeId) {
        console.log('No employeeId found in visit, showing no stocks');
        setAvailableStocks([]);
        return;
      }

      // First, get all stock assignments for this employee
      const assignmentsQuery = query(
        collection(db, 'StockMovements'),
        where('employeeId', '==', visit.employeeId),
        where('type', '==', 'assignment')
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      
      // Get unique stock IDs assigned to this employee
      const assignedStockIds = [...new Set(
        assignmentsSnapshot.docs.map(doc => doc.data().stockId)
      )];

      if (assignedStockIds.length === 0) {
        console.log('No stocks assigned to this employee');
        setAvailableStocks([]);
        return;
      }

      // Now get the actual stock data for assigned stocks
      const stocksQuery = query(
        collection(db, 'Stocks'),
        where('Durum', '==', 'aktif')
      );
      const stocksSnapshot = await getDocs(stocksQuery);
      const allStocksData = stocksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter to only show stocks assigned to this employee with available quantity
      const availableStocksData = allStocksData.filter(stock => {
        const isAssigned = assignedStockIds.includes(stock.id);
        const remaining = parseFloat(stock['Kalan']) || 0;
        return isAssigned && remaining > 0;
      });
      
      console.log('Available stocks for employee:', visit.employeeId, availableStocksData);
      setAvailableStocks(availableStocksData);
    } catch (error) {
      console.error('Error fetching stocks:', error);
      toast.error('Stoklar yüklenirken hata oluştu');
    }
  };

  // Load stock items when visit changes
  useEffect(() => {
    if (visit?.id) {
      // Load stock items from visit data
      setStockItems(visit.stockItems || []);
      setShipments(visit.shipments || []);
      // Fetch available stocks
      fetchAvailableStocks();
    } else {
      setStockItems([]);
      setShipments([]);
    }
  }, [visit?.id]);

  // Fetch hospitals and cities
  useEffect(() => {
    const fetchData = async () => {
      try {
        const centersCollection = collection(db, 'Centers');
        const centersSnapshot = await getDocs(centersCollection);

        const hospitalsList = centersSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            name: doc.data().Adı,
            // Kept alongside the name so picking a hospital can fill the city
            city: doc.data().İl || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr')); // Sort alphabetically with Turkish locale

        const uniqueCities = [
          ...new Set(
            centersSnapshot.docs
              .map((doc) => doc.data().İl)
              .filter((city) => city)
              .sort()
          ),
        ];

        // Fetch CRAs from ExternalPersonnels collection
        // Filter for personnel with "CRA" in either "Ünvan" or "Personel Tipi"
        const externalPersonnelsCollection = collection(db, 'ExternalPersonnels');
        const externalPersonnelsSnapshot = await getDocs(externalPersonnelsCollection);
        const crasList = externalPersonnelsSnapshot.docs
          .filter((doc) => {
            const data = doc.data();
            const unvan = (data['Ünvan'] || '').toUpperCase();
            const personnelType = (data['Personel Tipi'] || '').toUpperCase();
            const status = (data['Durum'] || '').toLowerCase();
            
            // Only include active personnel with CRA in title or personnel type
            return status === 'aktif' && (unvan.includes('CRA') || personnelType.includes('CRA'));
          })
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: `${data['Adı'] || ''} ${data['Soyadı'] || ''}`.trim(),
              unvan: data['Ünvan'] || '',
              personnelType: data['Personel Tipi'] || '',
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'tr')); // Sort alphabetically with Turkish locale

        setCras(crasList);

        setHospitals(hospitalsList);
        setCities(uniqueCities);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Centers has duplicate documents for the same hospital, which showed up as
  // the same name repeated in the list. They are merged into one option here;
  // `hospitals` still holds every document so stored ids keep resolving.
  const { options: hospitalOptions, canonicalIdById } = useMemo(
    () => dedupeCenters(hospitals, (hospital) => hospital.name),
    [hospitals]
  );

  // The centers this user works at on this project come first in the Hastane
  // list, so the usual choice is one click away instead of a long scroll.
  const myProjectCenters = useMemo(
    () => hospitalOptions.filter((option) => optionMatchesAnyCenterId(option, projectCenterIds)),
    [hospitalOptions, projectCenterIds]
  );
  const otherCenters = useMemo(
    () => hospitalOptions.filter((option) => !optionMatchesAnyCenterId(option, projectCenterIds)),
    [hospitalOptions, projectCenterIds]
  );

  // Hastane and Şehir were being retyped on every visit even though the project
  // already says which center the user works at. Fill them in from that
  // assignment, or failing that from the user's last visit on the same project.
  // Only empty fields are ever filled - a value already on the visit is kept.
  useEffect(() => {
    if (!visit?.projectId || !employeeId || isReadOnly) return;
    if (loading || hospitals.length === 0 || formData.hospital) return;

    let cancelled = false;

    const applyLocationDefaults = async () => {
      const centersById = new Map(
        hospitals.map((hospital) => [hospital.id, hospital])
      );

      const fill = (centerId, city, source) => {
        if (cancelled || !centerId) return;
        setFormData((prev) =>
          prev.hospital
            ? prev
            : {
                ...prev,
                hospital: centerId,
                city: prev.city || city || centersById.get(centerId)?.city || '',
              }
        );
        setLocationAutoFilledFrom(source);
      };

      // A single assigned center is unambiguous, so it wins.
      if (projectCenterIds.length === 1 && centersById.has(projectCenterIds[0])) {
        fill(projectCenterIds[0], centersById.get(projectCenterIds[0]).city, 'project');
        return;
      }

      try {
        const lastLocation = await fetchLastVisitLocation(
          visit.projectId,
          employeeId,
          visit.id
        );
        if (lastLocation) {
          fill(lastLocation.hospital, lastLocation.city, 'previous');
        }
      } catch (error) {
        console.error('Error resolving visit location defaults:', error);
      }
    };

    applyLocationDefaults();

    return () => {
      cancelled = true;
    };
  }, [
    visit?.id,
    visit?.projectId,
    employeeId,
    isReadOnly,
    loading,
    hospitals,
    projectCenterIds,
    formData.hospital,
  ]);

  // D — Tamamlanma Tarihleri still outstanding on this completed visit. Read
  // from the live form so a date typed in now clears its warning immediately.
  const pendingCompletionLabels = useMemo(
    () =>
      formData.visitType === 'home'
        ? []
        : getPendingCompletionLabels({
            status: visit?.status || formData.status,
            pendingCompletionFields: getMissingCompletionKeys(formData.visitEvaluation),
            visitEvaluation: formData.visitEvaluation,
          }),
    [visit?.status, formData.status, formData.visitType, formData.visitEvaluation]
  );

  // Şehir always follows Hastane, so the user only has to pick one of the two.
  const handleHospitalChange = (centerId) => {
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    const center = hospitals.find((hospital) => hospital.id === centerId);
    setLocationAutoFilledFrom('');
    setFormData((prev) => ({
      ...prev,
      hospital: centerId,
      city: center?.city || prev.city,
    }));
  };

  const handleInputChange = (field, value) => {
    // Prevent changes if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEvalChange = (field, value) => {
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      visitEvaluation: {
        ...prev.visitEvaluation,
        [field]: value,
      },
    }));
  };

  const handleTodoChange = async (key, value) => {
    // Prevent changes if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    try {
      const updatedTodoList = {
        ...formData.todoList,
        [key]: value,
      };

      // Update local state
      setFormData((prev) => ({
        ...prev,
        todoList: updatedTodoList,
      }));

      // Update Firestore
      const visitRef = doc(db, 'Visits', visit.id);
      await updateDoc(visitRef, {
        todoList: updatedTodoList,
        lastUpdated: new Date().toISOString(),
      });

      // Notify parent component
      if (onVisitUpdate) {
        onVisitUpdate({
          ...visit,
          todoList: updatedTodoList,
          lastUpdated: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error updating todo list:', error);
      toast.error('Yapılacaklar listesi güncellenirken bir hata oluştu');
    }
  };

  const handleSave = async () => {
    // Prevent saving if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    if (!visit?.id) return;

    // CRA seçimi artık hiçbir aşamada zorunlu değil: harcaması olan ziyaretler
    // de CRA seçilmeden kaydedilir (bkz. SHOW_CRA_APPROVAL_TAB).

    setSaving(true);
    try {
      const visitRef = doc(db, 'Visits', visit.id);

      // Get current data from Firebase to preserve expenses
      const currentDoc = await getDoc(visitRef);
      let currentData = {};
      if (currentDoc.exists()) {
        currentData = currentDoc.data();
      }

      // Compute travel totals from hours/minutes for storage
      const travelBefore = (Number(formData.visitEvaluation?.travelBeforeHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelBeforeMinutes) || 0);
      const travelAfter = (Number(formData.visitEvaluation?.travelAfterHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelAfterMinutes) || 0);

      // Build visitEvaluation without UI-only split fields, with computed totals
      const { travelBeforeHours, travelBeforeMinutes, travelAfterHours, travelAfterMinutes, ...evalWithoutSplits } = formData.visitEvaluation || {};
      const visitEvaluationForSave = {
        ...evalWithoutSplits,
        travelTimeBefore: travelBefore > 0 ? String(travelBefore) : (evalWithoutSplits.travelTimeBefore || ''),
        travelTimeAfter: travelAfter > 0 ? String(travelAfter) : (evalWithoutSplits.travelTimeAfter || ''),
        // Ensure clinicianFullName is also in visitEvaluation for ManualDataEntryModal compatibility
        clinicianFullName: formData.clinicianFullName || evalWithoutSplits.clinicianFullName || '',
      };

      const updatedData = {
        ...currentData, // Preserve existing data (including expenses)
        ...formData, // Update with form data
        // Ensure home visit top-level fields are saved
        visitStartDate: formData.visitStartDate || '',
        visitStartTime: formData.visitStartTime || '',
        visitEndDate: formData.visitEndDate || '',
        visitEndTime: formData.visitEndTime || '',
        clinicianFullName: formData.clinicianFullName || '',
        endTime: formData.visitEndTime || formData.endTime || '',
        visitEvaluation: visitEvaluationForSave,
        expenses: currentData.expenses || expenses, // Ensure expenses are preserved
        lastUpdated: new Date().toISOString(),
        // Filling a D-section date on an already completed visit drops it from
        // the pending list; the visit only carries what is still missing.
        pendingCompletionFields:
          (currentData.status || formData.status) && formData.visitType !== 'home'
            ? getMissingCompletionKeys(visitEvaluationForSave)
            : [],
      };

      console.log('Saving visit data:', {
        visitEvaluation: updatedData.visitEvaluation,
        clinicianFullName: updatedData.clinicianFullName,
        date: updatedData.date,
        startTime: updatedData.startTime,
        visitStartDate: updatedData.visitStartDate,
        visitEndDate: updatedData.visitEndDate,
      });

      await updateDoc(visitRef, updatedData);

      // Hibrit proje: tarih/hasta/merkez değişikliği unblinded çalışandaki eş
      // vizite de yansır. Senkron hatası kullanıcının kendi kaydını bozmaz.
      if (!isMirrorVisit(updatedData)) {
        try {
          const synced = await syncVisitMirrors(visit.id, updatedData);
          if (synced > 0) toast.info('Unblinded çalışanın takvimindeki eş vizit de güncellendi.');
        } catch (mirrorError) {
          console.error('Eş vizit güncellenemedi:', mirrorError);
          toast.error('Eş vizit güncellenemedi; unblinded çalışana haber verin.');
        }
      }

      // Notify parent component of the update
      if (onVisitUpdate) {
        onVisitUpdate({
          ...visit,
          ...updatedData,
        });
      }

      toast.success('Ziyaret başarıyla kaydedildi');
      resetForm(); // Reset form after successful save
      onHide();
    } catch (error) {
      console.error('Error saving visit:', error);
      toast.error('Ziyaret kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteVisit = async () => {
    // Prevent completing if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler düzenlenemez.');
      return;
    }

    // Create an array to store empty fields
    const emptyFields = [];

    // Check each required field
    if (!formData.visitType) emptyFields.push('Ziyaret Tipi');
    if (!formData.sourceDocument) emptyFields.push('Kaynak Döküman');
    if (!formData.date) emptyFields.push('Tarih');
    if (!formData.startTime) emptyFields.push('Visit Başlama');
    if (!formData.endTime) emptyFields.push('Visit Bitiş');
    if (!normalizePatientId(formData.patientId)) emptyFields.push('Hasta ID');
    if (!formData.doctor) emptyFields.push('Hekim');
    if (!formData.hospital) emptyFields.push('Hastane');
    if (!formData.city) emptyFields.push('Şehir');
    if (!formData.travelType) emptyFields.push('Seyahat Tipi');
    
    // CRA selection is NOT required to complete the visit

    // "Stok girişi yapılacak" işaretliyse, Stok sekmesinde en az bir malzeme
    // kaydı olmadan ziyaret tamamlanamaz. İşaretli değilse stok girişi
    // beklenmez; ziyaret normal şekilde kapanır.
    if (formData.todoList.stockEntryRequired && stockItems.length === 0) {
      toast.error(
        'Bu ziyaret için stok girişi gerekli olarak işaretlendi. Tamamlamadan önce Stok sekmesinden kullanılan malzemeyi ekleyin.',
        { duration: 5000, icon: '📦' }
      );
      setActiveTab('stock');
      return;
    }

    // Check if at least one todo item is completed
    const todoItems = Object.entries(formData.todoList);
    const completedTodos = todoItems.filter(([_, value]) => value === true);

    if (completedTodos.length === 0) {
      emptyFields.push(
        'En az bir yapılacaklar listesi maddesi tamamlanmalıdır'
      );
    }

    // ── Visit Değerlendirme validation ──
    const evalData = formData.visitEvaluation || {};

    if (formData.visitType === 'home') {
      // B — Pencere / Zamanlama
      if (!evalData.windowOpenDate) emptyFields.push('Visit Değerlendirme - Pencere Açılış');
      if (!evalData.targetDate) emptyFields.push('Visit Değerlendirme - Hedef Tarih');
      if (!evalData.windowCloseDate) emptyFields.push('Visit Değerlendirme - Pencere Kapanış');
      // C — Planlanan
      if (!formData.clinicianFullName) emptyFields.push('Visit Değerlendirme - Klinisyen');
      // D — Yolculuk Öncesi
      if (!evalData.travelBeforeHours && !evalData.travelBeforeMinutes) emptyFields.push('Visit Değerlendirme - Yolculuk Öncesi Süre');
      if (!evalData.travelDistanceBefore) emptyFields.push('Visit Değerlendirme - Yolculuk Öncesi Mesafe (km)');
      // E — Vizit Detayları
      if (!formData.visitStartDate) emptyFields.push('Visit Değerlendirme - Vizit Başlangıç Tarihi');
      if (!formData.visitStartTime) emptyFields.push('Visit Değerlendirme - Vizit Başlangıç Saati');
      if (!formData.visitEndDate) emptyFields.push('Visit Değerlendirme - Vizit Bitiş Tarihi');
      if (!formData.visitEndTime) emptyFields.push('Visit Değerlendirme - Vizit Bitiş Saati');
      if (!evalData.visitDurationHours && !evalData.visitDurationMinutes) emptyFields.push('Visit Değerlendirme - Vizit Süresi');
      // F — Yolculuk Sonrası
      if (!evalData.travelAfterHours && !evalData.travelAfterMinutes) emptyFields.push('Visit Değerlendirme - Yolculuk Sonrası Süre');
      // G — Sorun Bilgisi
      if (!evalData.issueOccurred) emptyFields.push('Visit Değerlendirme - Sorun Oluştu mu?');
      if (evalData.issueOccurred === 'yes') {
        if (!evalData.issueResultedInMiss) emptyFields.push('Visit Değerlendirme - Sorun Vizit Kaçırılmasına Neden Oldu mu?');
        if (!evalData.issueCategory) emptyFields.push('Visit Değerlendirme - Sorun Kaynağı');
      }
      // I — Kaynak Tarihleri
      if (!evalData.dateInitialSourceReceived) emptyFields.push('Visit Değerlendirme - İlk Kaynak Alınma Tarihi');
      if (!evalData.dateSourceFinalized) emptyFields.push('Visit Değerlendirme - Kaynak Kesinleşme Tarihi');
      // J — Sonuç
      if (!evalData.wasAeSaeTaken) emptyFields.push('Visit Değerlendirme - AE/SAE Alındı mı?');
      if (!evalData.dcfCreated) emptyFields.push('Visit Değerlendirme - DCF Oluşturuldu mu?');
    } else {
      // Hastane / Site visit evaluation
      // B — Hasta Durumu
      if (!evalData.screenFail) emptyFields.push('Visit Değerlendirme - Screen Fail');
      if (!evalData.dropPatient) emptyFields.push('Visit Değerlendirme - Drop Patient');
      // C — Protokol & CAPA
      if (!evalData.protocolDeviation) emptyFields.push('Visit Değerlendirme - Protocol Deviation');
      if (evalData.protocolDeviation === 'yes' && !evalData.capaOpened) emptyFields.push('Visit Değerlendirme - CAPA Açıldı mı?');
      // D — Tamamlanma Tarihleri deliberately does NOT block completion: the
      // e-CRF, source document and signature dates take weeks to collect and
      // were keeping finished visits open. Whatever is missing is recorded on
      // the visit as pendingCompletionFields and shown until it arrives.
      // E — Süreç & SLA
      if (!evalData.processEndDate) emptyFields.push('Visit Değerlendirme - Process End Date');
      if (!evalData.slaTarget) emptyFields.push('Visit Değerlendirme - SLA Target');
      // F — Sorun Takibi
      if (!evalData.issueOccurred) emptyFields.push('Visit Değerlendirme - Sorun Oluştu mu?');
      if (evalData.issueOccurred === 'yes') {
        if (!evalData.issueCategory) emptyFields.push('Visit Değerlendirme - Sorun Kategorisi');
        if (!evalData.issueDetails) emptyFields.push('Visit Değerlendirme - Sorun Detayları');
      }
    }

    // If there are empty fields, show error message
    if (emptyFields.length > 0) {
      toast.error(
        `Lütfen aşağıdaki alanları doldurunuz:\n${emptyFields.join('\n')}`,
        {
          duration: 5000,
          style: {
            whiteSpace: 'pre-line',
            maxWidth: '500px',
          },
        }
      );
      return;
    }

    // If validation passes, update Firebase
    if (!visit?.id) return;

    setCompleting(true);
    try {
      const visitRef = doc(db, 'Visits', visit.id);

      // Get current data from Firebase to preserve expenses
      const currentDoc = await getDoc(visitRef);
      let currentData = {};
      if (currentDoc.exists()) {
        currentData = currentDoc.data();
      }

      // Compute travel totals from hours/minutes for storage (same as handleSave)
      const travelBeforeComplete = (Number(formData.visitEvaluation?.travelBeforeHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelBeforeMinutes) || 0);
      const travelAfterComplete = (Number(formData.visitEvaluation?.travelAfterHours) || 0) * 60 + (Number(formData.visitEvaluation?.travelAfterMinutes) || 0);

      const { travelBeforeHours: _tbh, travelBeforeMinutes: _tbm, travelAfterHours: _tah, travelAfterMinutes: _tam, ...evalWithoutSplitsComplete } = formData.visitEvaluation || {};
      const visitEvaluationForComplete = {
        ...evalWithoutSplitsComplete,
        travelTimeBefore: travelBeforeComplete > 0 ? String(travelBeforeComplete) : (evalWithoutSplitsComplete.travelTimeBefore || ''),
        travelTimeAfter: travelAfterComplete > 0 ? String(travelAfterComplete) : (evalWithoutSplitsComplete.travelTimeAfter || ''),
        clinicianFullName: formData.clinicianFullName || evalWithoutSplitsComplete.clinicianFullName || '',
      };

      const updatedVisitData = {
        ...currentData, // Preserve existing data (including expenses)
        ...formData, // Update with form data
        visitStartDate: formData.visitStartDate || '',
        visitStartTime: formData.visitStartTime || '',
        visitEndDate: formData.visitEndDate || '',
        visitEndTime: formData.visitEndTime || '',
        clinicianFullName: formData.clinicianFullName || '',
        endTime: formData.visitEndTime || formData.endTime || '',
        visitEvaluation: visitEvaluationForComplete,
        expenses: currentData.expenses || expenses, // Ensure expenses are preserved
        lastUpdated: new Date().toISOString(),
        status: true,
        completedAt: new Date().toISOString(),
        // D — Tamamlanma Tarihleri that are still outstanding. Surfaced on the
        // calendars and in this modal until they are entered. Only hastane
        // visits have this section - the home visit form has no such fields.
        pendingCompletionFields:
          formData.visitType !== 'home'
            ? getMissingCompletionKeys(visitEvaluationForComplete)
            : [],
      };

      console.log(
        'Completing visit with expenses preserved:',
        updatedVisitData.expenses
      );

      await updateDoc(visitRef, updatedVisitData);

      // Call the update callback with the updated visit data
      onVisitUpdate({
        id: visit.id,
        ...updatedVisitData,
      });

      const pendingLabels = getPendingCompletionLabels({
        status: true,
        pendingCompletionFields: updatedVisitData.pendingCompletionFields,
        visitEvaluation: visitEvaluationForComplete,
      });

      if (pendingLabels.length > 0) {
        toast.success(
          `Ziyaret tamamlandı. Eksik olarak işaretlendi: ${pendingLabels.join(', ')} - tarihleri girdiğinizde uyarı kalkacak.`,
          { duration: 6000, style: { maxWidth: '500px' } }
        );
      } else {
        toast.success('Ziyaret başarıyla tamamlandı!');
      }
      resetForm(); // Reset form after successful completion
      onHide();
    } catch (error) {
      console.error('Error completing visit:', error);
      toast.error('Ziyaret tamamlanırken bir hata oluştu.');
    } finally {
      setCompleting(false);
    }
  };

  const handleSendCraApproval = async () => {
    // Prevent sending approval if it's a past visit and user is not admin
    if (isReadOnly) {
      toast.error('Geçmiş ziyaretler için CRA onayı gönderilemez.');
      return;
    }
    
    // Check if there are expenses
    const hasExpenses = expenses.length > 0;
    
    if (!formData.selectedCra) {
      if (hasExpenses) {
        // If there are expenses, CRA selection is mandatory
        toast.error('Harcama olduğu için CRA seçimi zorunludur!');
      } else {
        // If no expenses, CRA selection is optional but still required to send
        toast.error('Lütfen bir CRA seçiniz');
      }
      return;
    }
    
    try {
      setIsSendingCraApproval(true);
      const response = await fetch(
        'https://us-central1-habcare-8ecad.cloudfunctions.net/sendCraApprovalEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            craId: formData.selectedCra,
            visitDetails: formData, // Tüm visit detaylarını gönder
            senderName:
              (employeeData &&
                (employeeData.fullName ||
                  `${employeeData.eName || ''} ${
                    employeeData.eSurname || ''
                  }`.trim())) ||
              undefined,
          }),
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      try {
        if (visit?.id) {
          await updateDoc(doc(db, 'Visits', visit.id), {
            craApproval: {
              sent: true,
              sentAt: new Date().toISOString(),
              craId: formData.selectedCra,
            },
            lastUpdated: new Date().toISOString(),
          });
        }
      } catch (persistErr) {
        console.error('CRA approval persist failed:', persistErr);
      }
      setCraApprovalSent(true);
      setCraApprovalSentAt(new Date().toISOString());
      toast.success('CRA onayı başarıyla gönderildi!');
    } catch (error) {
      console.error('CRA onayı gönderilirken hata:', error);
      toast.error('CRA onayı gönderilirken bir hata oluştu.');
    } finally {
      setIsSendingCraApproval(false);
    }
  };

  // Function to delete the visit
  const handleDeleteVisit = async () => {
    if (!visit?.id) return;

    const confirmDelete = window.confirm(
      'Bu ziyareti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      const visitRef = doc(db, 'Visits', visit.id);
      await deleteDoc(visitRef);

      // Hibrit proje: bu vizitten türeyen, tamamlanmamış eş vizitler de silinir
      if (!isMirrorVisit(visit)) {
        try {
          const mirrors = await deleteVisitMirrors(visit.id);
          if (mirrors.deleted > 0) toast.info('Unblinded çalışanın takvimindeki eş vizit de silindi.');
          if (mirrors.kept > 0) toast.info('Tamamlanmış eş vizit korundu.');
        } catch (mirrorError) {
          console.error('Eş vizit silinemedi:', mirrorError);
          toast.error('Eş vizit silinemedi; unblinded çalışana haber verin.');
        }
      }

      toast.success('Ziyaret başarıyla silindi');

      // Notify parent component of the deletion
      if (onVisitUpdate) {
        onVisitUpdate({ id: visit.id, deleted: true });
      }

      onHide();
    } catch (error) {
      console.error('Error deleting visit:', error);
      toast.error('Ziyaret silinirken bir hata oluştu');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle stock item added
  const handleStockAdded = async (stockData) => {
    try {
      if (!visit || !visit.id) {
        toast.error('Ziyaret bilgisi bulunamadı');
        return;
      }

      console.log('Stock data received:', stockData);
      console.log('Current stockItems:', stockItems);

      const visitRef = doc(db, 'Visits', visit.id);
      
      // Create new stock item - ensure all fields are defined
      const newStockItem = {
        stockId: stockData.stockId || '',
        stockName: stockData.stockName || '',
        stockType: stockData.stockType || '',
        quantity: stockData.quantity || 0,
        reason: stockData.reason || '',
        addedAt: new Date().toISOString(),
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      console.log('New stock item to add:', newStockItem);

      // Update visit with new stock item - ensure stockItems is always an array
      const currentStockItems = Array.isArray(stockItems) ? stockItems : [];
      const updatedStockItems = [...currentStockItems, newStockItem];
      
      console.log('Updated stock items:', updatedStockItems);

      await updateDoc(visitRef, {
        stockItems: updatedStockItems,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      setStockItems(updatedStockItems);
      
      // Update stock quantity in Stocks collection
      const stockRef = doc(db, 'Stocks', stockData.stockId);
      const stockDoc = await getDoc(stockRef);
      
      if (stockDoc.exists()) {
        const currentStock = stockDoc.data();
        const currentRemaining = parseFloat(currentStock['Kalan']) || 0;
        const usedQuantity = parseFloat(stockData.quantity) || 0;
        const newRemaining = currentRemaining - usedQuantity;
        
        await updateDoc(stockRef, {
          'Kalan': newRemaining
        });

        // Resolve employee full name
        let resolvedEmployeeName = visit.employeeName || '';
        if (!resolvedEmployeeName && visit.employeeId) {
          try {
            const empDoc = await getDoc(doc(db, 'Employees', visit.employeeId));
            if (empDoc.exists()) {
              const empData = empDoc.data();
              resolvedEmployeeName = `${empData.eName || ''} ${empData.eSurname || ''}`.trim();
            }
          } catch (e) {
            console.error('Error fetching employee name:', e);
          }
        }

        // Kullanımın hangi müşteri/proje için yapıldığı: önce stok kartındaki
        // bağ, yoksa ziyaretin projesi. Stok raporu "hangi müşteriye ne kadar
        // gitti" sorusunu bu alanlardan cevaplıyor.
        let projectName = currentStock.projectName || formData.projectName || '';
        let customerName = currentStock.customerName || '';
        if (!customerName && visit.projectId) {
          try {
            const projectDoc = await getDoc(doc(db, 'Projects', visit.projectId));
            if (projectDoc.exists()) {
              const projectData = projectDoc.data();
              customerName = projectData.customer || '';
              if (!projectName) projectName = projectData.titleShort || '';
            }
          } catch (projectError) {
            console.error('Proje bilgisi alınamadı:', projectError);
          }
        }

        // Create movement record in StockMovements collection
        const movementRecord = {
          stockId: stockData.stockId,
          stockName: stockData.stockName || currentStock['Adı'] || '',
          employeeId: visit.employeeId || '',
          employeeName: resolvedEmployeeName,
          quantity: usedQuantity,
          note: `Ziyarette kullanıldı - ${stockData.reason || 'Sebep belirtilmedi'}`,
          date: new Date().toISOString(),
          type: 'visit_usage',
          visitId: visit.id,
          visitDate: visit.date || visit.firstVisitDate || '',
          patientId: visit.patientId || '',
          projectId: visit.projectId || '',
          projectName,
          customerName
        };

        // Save movement to StockMovements collection
        await addDoc(collection(db, 'StockMovements'), movementRecord);
      }

      toast.success('Malzeme başarıyla eklendi');
      
      // Refresh available stocks
      await fetchAvailableStocks();
      
      // Close modal after successful addition
      setShowStockModal(false);
    } catch (error) {
      console.error('Error adding stock item:', error);
      console.error('Error details:', error.message);
      toast.error(`Malzeme eklenirken hata oluştu: ${error.message}`);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  // Handle stock item removed
  const handleStockRemoved = async (stockItemId) => {
    const confirmRemove = window.confirm('Bu malzemeyi kaldırmak istediğinizden emin misiniz?');
    if (!confirmRemove) return;

    try {
      const visitRef = doc(db, 'Visits', visit.id);
      
      // Find the stock item to remove
      const stockItemToRemove = stockItems.find(item => item.id === stockItemId);
      if (!stockItemToRemove) return;

      // Update visit - remove stock item
      const updatedStockItems = stockItems.filter(item => item.id !== stockItemId);
      await updateDoc(visitRef, {
        stockItems: updatedStockItems,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      setStockItems(updatedStockItems);
      
      // Return stock quantity to Stocks collection
      const stockRef = doc(db, 'Stocks', stockItemToRemove.stockId);
      const stockDoc = await getDoc(stockRef);
      
      if (stockDoc.exists()) {
        const currentStock = stockDoc.data();
        const currentRemaining = parseFloat(currentStock['Kalan']) || 0;
        const returnedQuantity = parseFloat(stockItemToRemove.quantity) || 0;
        const newRemaining = currentRemaining + returnedQuantity;
        
        await updateDoc(stockRef, {
          'Kalan': newRemaining
        });

        // Resolve employee full name
        let resolvedReturnEmployeeName = visit.employeeName || '';
        if (!resolvedReturnEmployeeName && visit.employeeId) {
          try {
            const empDoc = await getDoc(doc(db, 'Employees', visit.employeeId));
            if (empDoc.exists()) {
              const empData = empDoc.data();
              resolvedReturnEmployeeName = `${empData.eName || ''} ${empData.eSurname || ''}`.trim();
            }
          } catch (e) {
            console.error('Error fetching employee name:', e);
          }
        }

        // Create return movement record in StockMovements collection
        const returnMovementRecord = {
          stockId: stockItemToRemove.stockId,
          stockName: stockItemToRemove.stockName || currentStock['Adı'] || '',
          employeeId: visit.employeeId || '',
          employeeName: resolvedReturnEmployeeName,
          quantity: -returnedQuantity, // Negative quantity to indicate return
          note: `Ziyaretten iade edildi - ${stockItemToRemove.reason || 'Sebep belirtilmedi'}`,
          date: new Date().toISOString(),
          type: 'visit_return',
          visitId: visit.id,
          visitDate: visit.date || visit.firstVisitDate || '',
          patientId: visit.patientId || ''
        };

        // Save return movement to StockMovements collection
        await addDoc(collection(db, 'StockMovements'), returnMovementRecord);
      }

      toast.success('Malzeme kaldırıldı');
      
      // Refresh available stocks
      fetchAvailableStocks();
    } catch (error) {
      console.error('Error removing stock item:', error);
      toast.error('Malzeme kaldırılırken hata oluştu');
    }
  };

  // Function to refresh expenses from Firebase
  const refreshExpensesFromFirebase = async () => {
    if (visit && visit.id) {
      try {
        console.log('Refreshing expenses from Firebase for visit:', visit.id);
        const visitRef = doc(db, 'Visits', visit.id);
        const visitDoc = await getDoc(visitRef);

        if (visitDoc.exists()) {
          const visitData = visitDoc.data();
          const latestExpenses = visitData.expenses || [];
          console.log('Latest expenses from Firebase:', latestExpenses);
          setExpenses(latestExpenses);

          // Also update formData to keep it in sync
          setFormData((prev) => ({
            ...prev,
            expenses: latestExpenses,
          }));

          // Also update the parent component
          if (onVisitUpdate) {
            onVisitUpdate({
              ...visit,
              ...visitData,
            });
          }
        }
      } catch (error) {
        console.error('Error refreshing expenses:', error);
      }
    }
  };

  const handleExpenseAdded = async (newExpense) => {
    console.log('handleExpenseAdded called with:', newExpense);

    // Update both expenses state and formData to keep them in sync
    setExpenses((prev) => {
      const updatedExpenses = [...prev, newExpense];
      console.log('Updated local expenses:', updatedExpenses);

      // Also update formData to include the new expense
      setFormData((prevFormData) => ({
        ...prevFormData,
        expenses: updatedExpenses,
      }));

      return updatedExpenses;
    });

    // Then refresh from Firebase to ensure we have the latest data
    setTimeout(async () => {
      await refreshExpensesFromFirebase();
    }, 500); // Small delay to ensure Firebase write has completed
  };

  return (
    <StyledModalContainer>
      <Toaster position="top-right" />
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        dialogClassName={`${styles.modalDialog} ${
          activeTab === 'expenses' ||
          activeTab === 'visitEvaluation' ||
          activeTab === 'shipments'
            ? styles.modalDialogWide
            : ''
        }`}
        contentClassName={styles.modalContent}
      >
        <div className={styles.customModalHeader}>
          <div className={styles.headerContent}>
            <h5 className={styles.modalTitle}>
              Saha Operasyonu{' '}
              {isRestrictedUser && (
                <span style={{ color: 'blue', fontSize: '0.8em' }}>
                  (Sadece Görüntüleme ve Silme)
                </span>
              )}
              {!isRestrictedUser && isReadOnly && (
                <span style={{ color: 'red', fontSize: '0.8em' }}>
                  (Salt Okunur)
                </span>
              )}
              {isPastVisit && isAdmin && !isRestrictedUser && (
                <span style={{ color: 'orange', fontSize: '0.8em' }}>
                  (Admin - Düzenlenebilir)
                </span>
              )}
            </h5>
            {!isRestrictedUser && (
              <Button
                className={styles.btnSuccess}
                onClick={handleCompleteVisit}
                disabled={completing || isReadOnly}
              >
                {completing ? 'Tamamlanıyor...' : '✓ Ziyareti Tamamla'}
              </Button>
            )}
          </div>
          {isMirrorVisit(visit) && (
            <p className={styles.mirrorNotice}>
              Hibrit proje eş viziti: bu vizit
              {visit.mirrorSourceEmployeeName ? ` ${visit.mirrorSourceEmployeeName} ` : ' blinded çalışan '}
              tarafından açılan vizitle birlikte takviminize otomatik eklendi.
              {visit.mirrorSourceDeleted ? ' Kaynak vizit sonradan silindi.' : ''}
            </p>
          )}

          {/* Completed while the D — Tamamlanma Tarihleri dates are still being
              collected. Stays up on every tab until they are entered. */}
          {pendingCompletionLabels.length > 0 && (
            <div className={styles.signaturePendingBanner}>
              <strong>Eksik tamamlanma tarihi: {pendingCompletionLabels.join(', ')}.</strong>{' '}
              Bu ziyaret tamamlandı, ancak bu tarihler henüz girilmedi. Visit
              Değerlendirme sekmesindeki "D — Tamamlanma Tarihleri" bölümünden
              girdiğinizde uyarı kalkar.
            </div>
          )}

          {/* Visit Completion Deadline Info */}
          {visit?.firstVisitDate && visit?.period && !isPastVisit && (
            (() => {
              const firstDate = new Date(visit.firstVisitDate);
              firstDate.setHours(0, 0, 0, 0);
              const period = visit.period;
              let lastDate;
              
              if (period >= 0) {
                lastDate = new Date(firstDate);
                lastDate.setDate(firstDate.getDate() + period - 1);
              } else {
                lastDate = new Date(firstDate);
              }
              
              lastDate.setHours(23, 59, 59, 999);
              const completionDeadline = new Date(lastDate);
              completionDeadline.setHours(completionDeadline.getHours() + 72);
              
              const today = new Date();
              const isAfterVisitEnd = today > lastDate;
              
              if (isAfterVisitEnd) {
                const hoursRemaining = Math.ceil((completionDeadline - today) / (1000 * 60 * 60));
                return (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    margin: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '1.2em' }}>⏰</span>
                    <div>
                      <strong>Ziyaret Süresi Doldu</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.9em' }}>
                        Ziyaret bitiş tarihi: {lastDate.toLocaleDateString('tr-TR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        <br />
                        Son tamamlama tarihi: {completionDeadline.toLocaleDateString('tr-TR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })} ({hoursRemaining} saat kaldı)
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          <Nav className={styles.navTabs}>
            <Nav.Link
              as="button"
              className={
                activeTab === 'informations'
                  ? styles.activeNavLink
                  : styles.navLink
              }
              onClick={() => setActiveTab('informations')}
            >
              Bilgiler
            </Nav.Link>
            <Nav.Link
              as="button"
              className={
                activeTab === 'todoList' ? styles.activeNavLink : styles.navLink
              }
              onClick={() => setActiveTab('todoList')}
            >
              Yapılacaklar
            </Nav.Link>
            <Nav.Link
              as="button"
              className={
                activeTab === 'visitForm'
                  ? styles.activeNavLink
                  : styles.navLink
              }
              onClick={() => setActiveTab('visitForm')}
            >
              Ziyaret Formu
            </Nav.Link>
            <Nav.Link
              as="button"
              className={
                activeTab === 'stock' ? styles.activeNavLink : styles.navLink
              }
              onClick={() => setActiveTab('stock')}
            >
              Stok
            </Nav.Link>
            <Nav.Link
              as="button"
              className={
                activeTab === 'shipments' ? styles.activeNavLink : styles.navLink
              }
              onClick={() => setActiveTab('shipments')}
            >
              Kargo Takip
              {shipments.length > 0 && (
                <span className={styles.navBadge}>{shipments.length}</span>
              )}
            </Nav.Link>
            <Nav.Link
              as="button"
              className={
                activeTab === 'expenses' ? styles.activeNavLink : styles.navLink
              }
              onClick={() => setActiveTab('expenses')}
            >
              Harcamalar
            </Nav.Link>
            <Nav.Link
              as="button"
              className={
                activeTab === 'visitEvaluation'
                  ? styles.activeNavLink
                  : styles.navLink
              }
              onClick={() => setActiveTab('visitEvaluation')}
            >
              Visit Değerlendirme
            </Nav.Link>
            {SHOW_CRA_APPROVAL_TAB && (
              <Nav.Link
                as="button"
                className={
                  activeTab === 'craApproval'
                    ? styles.activeNavLink
                    : styles.navLink
                }
                onClick={() => setActiveTab('craApproval')}
              >
                CRA Onay
              </Nav.Link>
            )}
          </Nav>
        </div>

        <div className={styles.customModalBody}>
          <div className={styles.tabContent}>
            {activeTab === 'informations' && (
              <div>
                <div className={styles.projectInfo}>
                  <div className={styles.infoItem}>
                    <h4 className={styles.infoHeading}>Proje Adı</h4>
                    <p className={styles.infoText}>
                      {projectTitle || 'Proje bilgisi yüklenemedi'}
                    </p>
                  </div>

                  <div className={styles.infoItem}>
                    <h4 className={styles.infoHeading}>Proje Fazı</h4>
                    <p className={styles.infoText}>
                      {projectPhase || 'Belirtilmemiş'}
                    </p>
                  </div>

                  <div className={styles.infoItem}>
                    <h4 className={styles.infoHeading}>
                      Proje Koordinatör Merkezi Tipi
                    </h4>
                    <p className={styles.infoText}>
                      {projectCentersType || 'Belirtilmemiş'}
                    </p>
                  </div>

                  <div className={styles.infoItem}>
                    <h4 className={styles.infoHeading}>Hasta ID</h4>
                    <p className={styles.infoText}>
                      {normalizePatientId(formData.patientId) || 'Belirtilmemiş'}
                    </p>
                  </div>

                  <div className={styles.infoItem}>
                    <h4 className={styles.infoHeading}>Kaçıncı Visit</h4>
                    <p className={styles.infoText}>
                      {formData.visitNumber ? `${formData.visitNumber}. Visit` : 'Belirtilmemiş'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'visitForm' && (
              <Form>
                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Ziyaret Tipi
                      </Form.Label>
                      <Form.Select
                        className={styles.formSelect}
                        value={formData.visitType}
                        onChange={(e) =>
                          handleInputChange('visitType', e.target.value)
                        }
                        disabled={isReadOnly}
                      >
                        <option>Lütfen Seçiniz</option>
                        <option value="hastane">Hastane Vizit</option>
                        <option value="home">Home Vizit</option>
                        <option value="telefon">Telefon Vizit</option>
                        <option value="unplanning">Unplanning Vizit</option>
                        <option value="handover">Handover Vizit</option>
                      </Form.Select>
                    </div>
                  </Col>
                  {formData.visitType === 'hastane' && (
                    <Col md={6}>
                      <div className={styles.formGroup}>
                        <Form.Check
                          type="checkbox"
                          id="screenFailCheck"
                          label="Screen Fail mi?"
                          checked={formData.screenFail === true}
                          onChange={(e) =>
                            handleInputChange('screenFail', e.target.checked)
                          }
                          disabled={isReadOnly}
                          className={styles.screenFailCheck}
                        />
                        {formData.screenFail === true && (
                          <small className={styles.screenFailNote}>
                            Screen fail olarak işaretlenen kayıtlar ziyaret olarak sayılmaz, yapılan iş olarak kaydedilir.
                          </small>
                        )}
                      </div>
                    </Col>
                  )}
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Kaynak Döküman
                      </Form.Label>
                      <Form.Select
                        className={styles.formSelect}
                        value={formData.sourceDocument}
                        onChange={(e) =>
                          handleInputChange('sourceDocument', e.target.value)
                        }
                        disabled={isReadOnly}
                      >
                        <option>Lütfen Seçiniz</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </Form.Select>
                    </div>
                  </Col>
                  {/* Conditional Source Document Completion Time field */}
                  {formData.sourceDocument === 'yes' && (
                    <Col md={6}>
                      <div className={styles.formGroup}>
                        <Form.Label className={styles.formLabel}>
                          Kaynak Döküman Tamamlanma Saati
                        </Form.Label>
                        <Form.Control
                          type="time"
                          className={styles.formControl}
                          value={formData.sourceDocumentCompletionTime || ''}
                          onChange={(e) =>
                            handleInputChange('sourceDocumentCompletionTime', e.target.value)
                          }
                          disabled={isReadOnly}
                        />
                      </div>
                    </Col>
                  )}
                </Row>

                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Visit Başlangıç Tarihi
                      </Form.Label>
                      <Form.Control
                        type="date"
                        className={styles.formControl}
                        value={formData.date}
                        onChange={(e) =>
                          handleInputChange('date', e.target.value)
                        }
                        disabled={isReadOnly || !!formData.date}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Visit Başlangıç Saati
                      </Form.Label>
                      <Form.Control
                        type="time"
                        className={styles.formControl}
                        value={formData.startTime}
                        onChange={(e) =>
                          handleInputChange('startTime', e.target.value)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Visit Bitiş Tarihi
                      </Form.Label>
                      <Form.Control
                        type="date"
                        className={styles.formControl}
                        value={formData.visitEvaluation?.visitEndDate || formData.date}
                        onChange={(e) =>
                          handleInputChange('visitEvaluation.visitEndDate', e.target.value)
                        }
                        disabled={isReadOnly || !!(formData.visitEvaluation?.visitEndDate)}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Visit Bitiş Saati
                      </Form.Label>
                      <Form.Control
                        type="time"
                        className={styles.formControl}
                        value={formData.endTime}
                        onChange={(e) =>
                          handleInputChange('endTime', e.target.value)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Hasta ID
                      </Form.Label>
                      {loadingPatients ? (
                        <Form.Control
                          type="text"
                          className={styles.formControl}
                          value="Hastalar yükleniyor..."
                          disabled
                        />
                      ) : projectPatients.length > 0 ? (
                        <Form.Select
                          className={styles.formSelect}
                          value={normalizePatientId(formData.patientId)}
                          onChange={(e) =>
                            handleInputChange('patientId', e.target.value)
                          }
                          disabled={isReadOnly}
                        >
                          <option value="">Hasta Seçiniz</option>
                          {projectPatients.map((patient) => (
                            <option key={patient.id} value={patient.patientId}>
                              {patient.patientId} {patient.status ? `(${patient.status})` : ''}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          type="text"
                          className={styles.formControl}
                          value={normalizePatientId(formData.patientId)}
                          onChange={(e) =>
                            handleInputChange('patientId', e.target.value)
                          }
                          disabled={isReadOnly}
                          placeholder="Size ait kayıtlı hasta yok - manuel giriş yapabilirsiniz"
                        />
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Kaçıncı Visit
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        className={styles.formControl}
                        value={formData.visitNumber}
                        onChange={(e) =>
                          handleInputChange('visitNumber', e.target.value)
                        }
                        disabled={isReadOnly}
                        placeholder="Örn: 1, 2, 3..."
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Hekim
                      </Form.Label>
                      <Form.Control
                        type="text"
                        className={styles.formControl}
                        value={formData.doctor}
                        onChange={(e) =>
                          handleInputChange('doctor', e.target.value)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Hastane
                      </Form.Label>
                      <Form.Select
                        className={styles.formSelect}
                        disabled={loading || isReadOnly}
                        // A visit saved against a duplicate document still has
                        // to show its merged option as the selected one.
                        value={resolveCanonicalCenterId(canonicalIdById, formData.hospital)}
                        onChange={(e) => handleHospitalChange(e.target.value)}
                      >
                        <option value="">Lütfen Seçiniz</option>
                        {myProjectCenters.length > 0 && (
                          <optgroup label="Bu projedeki merkezleriniz">
                            {myProjectCenters.map((hospital) => (
                              <option key={hospital.id} value={hospital.id}>
                                {hospital.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup
                          label={
                            myProjectCenters.length > 0
                              ? 'Diğer merkezler'
                              : 'Merkezler'
                          }
                        >
                          {otherCenters.map((hospital) => (
                            <option key={hospital.id} value={hospital.id}>
                              {hospital.name}
                            </option>
                          ))}
                        </optgroup>
                      </Form.Select>
                      {loading && <small>Hastaneler yükleniyor...</small>}
                      {!loading && locationAutoFilledFrom && (
                        <small className={styles.autoFillHint}>
                          {locationAutoFilledFrom === 'project'
                            ? 'Proje atamanızdan dolduruldu, değiştirebilirsiniz.'
                            : 'Son ziyaretinizden dolduruldu, değiştirebilirsiniz.'}
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Şehir
                      </Form.Label>
                      <Form.Select
                        className={styles.formSelect}
                        disabled={loading || isReadOnly}
                        value={formData.city}
                        onChange={(e) =>
                          handleInputChange('city', e.target.value)
                        }
                      >
                        <option value="">Lütfen Seçiniz</option>
                        {cities.map((city, index) => (
                          <option key={index} value={city}>
                            {city}
                          </option>
                        ))}
                      </Form.Select>
                      {loading && <small>Şehirler yükleniyor...</small>}
                      {!loading && (
                        <small className={styles.autoFillHint}>
                          Hastane seçtiğinizde otomatik güncellenir.
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Seyahat Tipi
                      </Form.Label>
                      <Form.Select
                        className={styles.formSelect}
                        value={formData.travelType}
                        onChange={(e) =>
                          handleInputChange('travelType', e.target.value)
                        }
                        disabled={isReadOnly}
                      >
                        <option>Lütfen Seçiniz</option>
                        <option value="local">Şehir İçi</option>
                        <option value="outside">Şehir Dışı</option>
                      </Form.Select>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <div className={styles.formGroup}>
                      <Form.Label className={styles.formLabel}>
                        Bilgi
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        className={styles.formControl}
                        value={formData.information}
                        onChange={(e) =>
                          handleInputChange('information', e.target.value)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                  </Col>
                </Row>
              </Form>
            )}

            {activeTab === 'todoList' && (
              <div className={styles.todoSection}>
                <div className={styles.todoCategory}>
                  <h3 className={styles.todoTitle}>
                    Ön Ziyaret Kontrol Listesi
                  </h3>
                  <div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Hasta Arandı mı?"
                        id="pre_patientCalled"
                        checked={formData.todoList.pre_patientCalled}
                        onChange={(e) =>
                          handleTodoChange(
                            'pre_patientCalled',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Hemşirenin transferleri ayarlandı mı?"
                        id="pre_nurseTransferArranged"
                        checked={formData.todoList.pre_nurseTransferArranged}
                        onChange={(e) =>
                          handleTodoChange(
                            'pre_nurseTransferArranged',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Visit sırasında toplanan örnekler için kurye ve kurye malzemeleri (buz, kuru buz, kutular vb.) ayarlandı mı?"
                        id="pre_courierArranged"
                        checked={formData.todoList.pre_courierArranged}
                        onChange={(e) =>
                          handleTodoChange(
                            'pre_courierArranged',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="İlacın sevkiyatı ile ilgili hastane onayı alındı mı?"
                        id="pre_hospitalApproval"
                        checked={formData.todoList.pre_hospitalApproval}
                        onChange={(e) =>
                          handleTodoChange(
                            'pre_hospitalApproval',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Hemşire visitte kullanacağı ekipmanları hazırladı mı?"
                        id="pre_nurseEquipmentReady"
                        checked={formData.todoList.pre_nurseEquipmentReady}
                        onChange={(e) =>
                          handleTodoChange(
                            'pre_nurseEquipmentReady',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="İhtiyaç durumunda konaklama planlandı mı?"
                        id="pre_accommodationPlanned"
                        checked={formData.todoList.pre_accommodationPlanned}
                        onChange={(e) =>
                          handleTodoChange(
                            'pre_accommodationPlanned',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.todoCategory}>
                  <h3 className={styles.todoTitle}>Yapılacaklar</h3>
                  <div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Ev ziyareti için hasta onam formu alındı mı...!"
                        id="patientConsent"
                        checked={formData.todoList.patientConsent}
                        onChange={(e) =>
                          handleTodoChange('patientConsent', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Protokol güncellemesi varmı? Varsa yeni onamlar alındı mı?"
                        id="protocolUpdate"
                        checked={formData.todoList.protocolUpdate}
                        onChange={(e) =>
                          handleTodoChange('protocolUpdate', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="KVKK bilgilendirme onayı kabul edildi mi?"
                        id="kvkkApproval"
                        checked={formData.todoList.kvkkApproval}
                        onChange={(e) =>
                          handleTodoChange('kvkkApproval', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Ziyaret numarası kontrol edildi mi?"
                        id="visitNumberCheck"
                        checked={formData.todoList.visitNumberCheck}
                        onChange={(e) =>
                          handleTodoChange('visitNumberCheck', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Proje kontrol edildi mi?"
                        id="projectCheck"
                        checked={formData.todoList.projectCheck}
                        onChange={(e) =>
                          handleTodoChange('projectCheck', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Work flow takibi yapıldı mı?"
                        id="workflowCheck"
                        checked={formData.todoList.workflowCheck}
                        onChange={(e) =>
                          handleTodoChange('workflowCheck', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Advers event var ise sınıflandırılması, takibi, raporlaması yapıldı mı?"
                        id="adverseEventCheck"
                        checked={formData.todoList.adverseEventCheck}
                        onChange={(e) =>
                          handleTodoChange(
                            'adverseEventCheck',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Eşlik eden ilaç takibi yapıldı mı?"
                        id="medicationCheck"
                        checked={formData.todoList.medicationCheck}
                        onChange={(e) =>
                          handleTodoChange('medicationCheck', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Kaynak dökümanlar dolduruldu mu?"
                        id="sourceDocuments"
                        checked={formData.todoList.sourceDocuments}
                        onChange={(e) =>
                          handleTodoChange('sourceDocuments', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Ziyaret sırasında doldurulan kaynak dökümanlar için sponsor proje müdüründen paylaşım onayı alındı mı?"
                        id="sourceDocumentsApproval"
                        checked={formData.todoList.sourceDocumentsApproval}
                        onChange={(e) =>
                          handleTodoChange(
                            'sourceDocumentsApproval',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Ziyaret sırasında doldurulan kaynak dökümanlar çalışma ekibi ile e-posta olarak paylaşıldı mı?"
                        id="sourceDocumentsShared"
                        checked={formData.todoList.sourceDocumentsShared}
                        onChange={(e) =>
                          handleTodoChange(
                            'sourceDocumentsShared',
                            e.target.checked
                          )
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Transfer formu dolduruldu mu?"
                        id="transferForm"
                        checked={formData.todoList.transferForm}
                        onChange={(e) =>
                          handleTodoChange('transferForm', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className={styles.formCheck}>
                      <Form.Check
                        type="checkbox"
                        label="Kaynak dökümanlar kargolandı mı?"
                        id="documentShipping"
                        checked={formData.todoList.documentShipping}
                        onChange={(e) =>
                          handleTodoChange('documentShipping', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                    </div>

                    {/* Stok girişi: işaretlenirse ziyaret, Stok sekmesine
                        malzeme girilmeden tamamlanamaz. */}
                    <div className={`${styles.formCheck} ${styles.stockTodoItem}`}>
                      <Form.Check
                        type="checkbox"
                        label="Stok girişi yapılacak mı?"
                        id="stockEntryRequired"
                        checked={formData.todoList.stockEntryRequired}
                        onChange={(e) =>
                          handleTodoChange('stockEntryRequired', e.target.checked)
                        }
                        disabled={isReadOnly}
                      />
                      <div className={styles.stockTodoHint}>
                        <button
                          type="button"
                          className={styles.stockTodoLink}
                          onClick={() => setActiveTab('stock')}
                        >
                          Stok sekmesine git
                        </button>
                        {formData.todoList.stockEntryRequired && (
                          <span
                            className={
                              stockItems.length > 0
                                ? styles.stockTodoDone
                                : styles.stockTodoPending
                            }
                          >
                            {stockItems.length > 0
                              ? `${stockItems.length} malzeme girildi`
                              : 'Henüz malzeme girilmedi — ziyaret tamamlanamaz'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stock' && (
              <div>
                <Button
                  className={`${styles.btnPrimary} float-end`}
                  style={{ marginBottom: '1rem' }}
                  disabled={isReadOnly}
                  onClick={() => setShowStockModal(true)}
                >
                  + Malzeme Ekle
                </Button>
                <Table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Malzeme Adı</th>
                      <th>Stok Tipi</th>
                      <th>Kullanım Sayısı</th>
                      <th>Kullanım Nedeni</th>
                      <th>Eklenme Tarihi</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.length > 0 ? (
                      stockItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.stockName}</td>
                          <td>{item.stockType || '-'}</td>
                          <td>{item.quantity}</td>
                          <td>{item.reason || '-'}</td>
                          <td>{item.addedAt ? new Date(item.addedAt).toLocaleString('tr-TR') : '-'}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleStockRemoved(item.id)}
                              disabled={isReadOnly}
                            >
                              Kaldır
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className={styles.emptyState}>
                          Henüz malzeme eklenmemiş
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                
                {/* Stock Add Modal */}
                {showStockModal && (
                  <StockAddModal
                    show={showStockModal}
                    onHide={() => setShowStockModal(false)}
                    availableStocks={availableStocks}
                    onStockAdded={handleStockAdded}
                  />
                )}
              </div>
            )}

            {activeTab === 'shipments' && (
              <ShipmentTracking
                visitId={visit?.id}
                shipments={shipments}
                onShipmentsChange={setShipments}
                isReadOnly={isReadOnly}
                currentUserName={`${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim()}
              />
            )}

            {activeTab === 'expenses' && (
              <div>
                <div
                  style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    className={`${styles.btnSecondary}`}
                    onClick={refreshExpensesFromFirebase}
                    disabled={isReadOnly}
                    style={{ fontSize: '0.8rem' }}
                  >
                    🔄 Yenile
                  </Button>
                  <Button
                    className={`${styles.btnPrimary}`}
                    onClick={() => setShowExpenseModal(true)}
                    disabled={isReadOnly}
                  >
                    + Masraf Ekle
                  </Button>
                </div>
                <Table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Ad</th>
                      <th>Tip</th>
                      <th>Maliyet</th>
                      <th>Bilgi</th>
                      <th>Dosyalar</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      console.log(
                        'Rendering expenses table with expenses:',
                        expenses
                      );
                      console.log('Expenses length:', expenses.length);
                      return null;
                    })()}
                    {expenses.length > 0 ? (
                      expenses.map((expense, index) => (
                        <tr key={index}>
                          <td>{expense.name}</td>
                          <td>
                            {expense.type === 'transportation' && 'Ulaşım'}
                            {expense.type === 'accommodation' && 'Konaklama'}
                            {expense.type === 'meal' && 'Yemek'}
                            {expense.type === 'supplies' && 'Malzeme'}
                            {expense.type === 'other' && 'Diğer'}
                          </td>
                          <td>{expense.cost.toFixed(2)} TL</td>
                          <td>{expense.information || '-'}</td>
                          <td>
                            {expense.attachments &&
                            expense.attachments.length > 0 ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                }}
                              >
                                {expense.attachments.map(
                                  (attachment, attachIndex) => (
                                    <button
                                      key={attachIndex}
                                      onClick={() =>
                                        window.open(attachment.url, '_blank')
                                      }
                                      style={{
                                        background: 'none',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        color: '#4299e1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        textAlign: 'left',
                                        transition: 'all 0.2s ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor =
                                          '#edf2f7';
                                        e.target.style.borderColor = '#4299e1';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor =
                                          'transparent';
                                        e.target.style.borderColor = '#e2e8f0';
                                      }}
                                      title={`${attachment.name} - ${
                                        attachment.size
                                          ? Math.round(attachment.size / 1024) +
                                            ' KB'
                                          : ''
                                      }`}
                                    >
                                      <span>
                                        {attachment.type?.startsWith('image/')
                                          ? '🖼️'
                                          : attachment.type ===
                                            'application/pdf'
                                          ? '📄'
                                          : '📎'}
                                      </span>
                                      <span
                                        style={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          maxWidth: '100px',
                                        }}
                                      >
                                        {attachment.name || 'Dosya'}
                                      </span>
                                    </button>
                                  )
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#718096' }}>-</span>
                            )}
                          </td>
                          <td>
                            {new Date(expense.createdAt).toLocaleDateString(
                              'tr-TR'
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className={styles.emptyState}>
                          Henüz masraf yok
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <ExpenseModal
                  show={showExpenseModal}
                  onHide={() => {
                    console.log('Closing ExpenseModal');
                    setShowExpenseModal(false);
                  }}
                  visitId={visit?.id}
                  onExpenseAdded={(newExpense) => {
                    console.log(
                      'ExpenseModal onExpenseAdded called with:',
                      newExpense
                    );
                    handleExpenseAdded(newExpense);
                  }}
                />
              </div>
            )}

            {activeTab === 'visitEvaluation' && (
              <div>
                {/* ── Header ─────────────────────────────────────────── */}
                {(() => {
                  const visitTypeLabel =
                    formData.visitType === 'hastane' ? 'Hastane Vizit' :
                    formData.visitType === 'home'    ? 'Home Vizit'    :
                    formData.visitType === 'telefon' ? 'Telefon Vizit' :
                    formData.visitType === 'unplanning' ? 'Unplanning Vizit' :
                    formData.visitType === 'handover'   ? 'Handover Vizit'   :
                    'Belirtilmemiş';

                  const visitDuration = (() => {
                    if (!formData.startTime || !formData.endTime) return '-';
                    const [sh, sm] = formData.startTime.split(':').map(Number);
                    const [eh, em] = formData.endTime.split(':').map(Number);
                    const diffMins = (eh * 60 + em) - (sh * 60 + sm);
                    if (diffMins <= 0) return '-';
                    return (diffMins / 60).toFixed(2);
                  })();

                  const cycleTime = (() => {
                    if (!formData.date || !formData.visitEvaluation?.processEndDate) return null;
                    const start = new Date(formData.date);
                    const end   = new Date(formData.visitEvaluation.processEndDate);
                    const diff  = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                    return diff >= 0 ? diff : null;
                  })();

                  const slaTarget = parseFloat(formData.visitEvaluation?.slaTarget) || null;
                  const slaMet      = cycleTime !== null && slaTarget !== null ? cycleTime <= slaTarget : null;
                  const slaOverdue  = cycleTime !== null && slaTarget !== null ? cycleTime > slaTarget  : null;

                  const sectionStyle = {
                    borderRadius: '10px',
                    padding: '1.25rem',
                    marginBottom: '1.25rem',
                    border: '1px solid',
                  };

                  return (
                    <Form>
                      {/* Summary bar */}
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                        <div style={{ background: '#edf2f7', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.875rem' }}>
                          <span style={{ color: '#718096' }}>Ziyaret Tipi: </span>
                          <strong>{visitTypeLabel}</strong>
                        </div>
                        <div style={{ background: '#edf2f7', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.875rem' }}>
                          <span style={{ color: '#718096' }}>Visit Süresi: </span>
                          <strong>{visitDuration !== '-' ? `${visitDuration} saat` : '-'}</strong>
                        </div>
                        {cycleTime !== null && (
                          <div style={{ background: '#edf2f7', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.875rem' }}>
                            <span style={{ color: '#718096' }}>Cycle Time: </span>
                            <strong>{cycleTime} gün</strong>
                          </div>
                        )}
                        {slaMet !== null && (
                          <div style={{ background: slaMet ? '#c6f6d5' : '#fed7d7', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.875rem' }}>
                            <span style={{ color: slaMet ? '#276749' : '#c53030' }}>
                              SLA: <strong>{slaMet ? 'Karşılandı' : 'Aşıldı'}</strong>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* ═══════════════════════════════════════════════════
                          HOME VISIT — Enhanced Evaluation Layout
                         ═══════════════════════════════════════════════════ */}
                      {formData.visitType === 'home' ? (
                        <>
                          {/* ── Section A: Kimlik (Identity - Read Only) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionScheduling}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              🆔 A — Kimlik
                            </h6>
                            <Row>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Hasta ID</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={normalizePatientId(formData.patientId)}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit ID</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.visitNumber || ''}
                                    disabled readOnly />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section B: Pencere / Zamanlama (Window / Timing) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionScheduling}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              📅 B — Pencere / Zamanlama
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Pencere Açılış</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.windowOpenDate || ''}
                                    onChange={(e) => handleEvalChange('windowOpenDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Hedef Tarih</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.targetDate || ''}
                                    onChange={(e) => handleEvalChange('targetDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Pencere Kapanış</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.windowCloseDate || ''}
                                    onChange={(e) => handleEvalChange('windowCloseDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section C: Planlanan (Scheduled) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionTravel}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              📋 C — Planlanan
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Klinisyen</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.clinicianFullName || ''}
                                    onChange={(e) => {
                                      handleInputChange('clinicianFullName', e.target.value);
                                      handleEvalChange('clinicianFullName', e.target.value);
                                    }}
                                    disabled={isReadOnly}
                                    placeholder="Klinisyen adı soyadı" />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Planlanan Tarih</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.date || ''}
                                    onChange={(e) => handleInputChange('date', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Planlanan Saat</Form.Label>
                                  <Form.Control type="time" className={styles.formControl}
                                    value={formData.startTime || ''}
                                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section D: Yolculuk Öncesi (Travel Before) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionTravel}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              🚗 D — Yolculuk Öncesi
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Saat</Form.Label>
                                  <Form.Control type="number" min="0" className={styles.formControl}
                                    value={formData.visitEvaluation?.travelBeforeHours || ''}
                                    onChange={(e) => handleEvalChange('travelBeforeHours', e.target.value)}
                                    disabled={isReadOnly} placeholder="Saat" />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Dakika</Form.Label>
                                  <Form.Control type="number" min="0" max="59" className={styles.formControl}
                                    value={formData.visitEvaluation?.travelBeforeMinutes || ''}
                                    onChange={(e) => handleEvalChange('travelBeforeMinutes', e.target.value)}
                                    disabled={isReadOnly} placeholder="Dakika" />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Mesafe (km)</Form.Label>
                                  <Form.Control type="number" min="0" step="0.1" className={styles.formControl}
                                    value={formData.visitEvaluation?.travelDistanceBefore || ''}
                                    onChange={(e) => handleEvalChange('travelDistanceBefore', e.target.value)}
                                    disabled={isReadOnly} placeholder="km" />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section E: Vizit Detayları (Visit Details) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionClinical}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              🏥 E — Vizit Detayları
                            </h6>
                            <Row>
                              <Col md={3}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit Başlangıç Tarihi</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitStartDate || ''}
                                    onChange={(e) => handleInputChange('visitStartDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit Başlangıç Saati</Form.Label>
                                  <Form.Control type="time" className={styles.formControl}
                                    value={formData.visitStartTime || ''}
                                    onChange={(e) => handleInputChange('visitStartTime', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit Bitiş Tarihi</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEndDate || ''}
                                    onChange={(e) => handleInputChange('visitEndDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit Bitiş Saati</Form.Label>
                                  <Form.Control type="time" className={styles.formControl}
                                    value={formData.visitEndTime || ''}
                                    onChange={(e) => handleInputChange('visitEndTime', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit Süresi (Saat)</Form.Label>
                                  <Form.Control type="number" min="0" className={styles.formControl}
                                    value={formData.visitEvaluation?.visitDurationHours || ''}
                                    onChange={(e) => handleEvalChange('visitDurationHours', e.target.value)}
                                    disabled={isReadOnly} placeholder="Saat" />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Vizit Süresi (Dakika)</Form.Label>
                                  <Form.Control type="number" min="0" max="59" className={styles.formControl}
                                    value={formData.visitEvaluation?.visitDurationMinutes || ''}
                                    onChange={(e) => handleEvalChange('visitDurationMinutes', e.target.value)}
                                    disabled={isReadOnly} placeholder="Dakika" />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section F: Yolculuk Sonrası (Travel After) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionTravel}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              🚗 F — Yolculuk Sonrası
                            </h6>
                            <Row>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Saat</Form.Label>
                                  <Form.Control type="number" min="0" className={styles.formControl}
                                    value={formData.visitEvaluation?.travelAfterHours || ''}
                                    onChange={(e) => handleEvalChange('travelAfterHours', e.target.value)}
                                    disabled={isReadOnly} placeholder="Saat" />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Dakika</Form.Label>
                                  <Form.Control type="number" min="0" max="59" className={styles.formControl}
                                    value={formData.visitEvaluation?.travelAfterMinutes || ''}
                                    onChange={(e) => handleEvalChange('travelAfterMinutes', e.target.value)}
                                    disabled={isReadOnly} placeholder="Dakika" />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section G: Sorun Bilgisi (Issue Info) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionIssues}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              ⚠️ G — Sorun Bilgisi
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Sorun Oluştu mu?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.issueOccurred || ''}
                                    onChange={(e) => handleEvalChange('issueOccurred', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Sorun Vizit Kaçırılmasına Neden Oldu mu?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.issueResultedInMiss || ''}
                                    onChange={(e) => handleEvalChange('issueResultedInMiss', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Sorun Kaynağı</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.visitEvaluation?.issueCategory || ''}
                                    onChange={(e) => handleEvalChange('issueCategory', e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="Sorun kaynağı..." />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section H: Kalite İncelemesi (Quality Review) — Only visible to admins/SMO leads ── */}
                          {isAdmin && (
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionQuality}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              🛡️ H — Kalite İncelemesi
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>SMO Düzeltme Talepleri</Form.Label>
                                  <Form.Control type="number" min="0" className={styles.formControl}
                                    value={formData.visitEvaluation?.smoCorrectionRequests || ''}
                                    onChange={(e) => handleEvalChange('smoCorrectionRequests', e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="Düzeltme talep sayısı" />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Klinik PM Düzeltmeleri</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.visitEvaluation?.qualityManagerCheck || ''}
                                    onChange={(e) => handleEvalChange('qualityManagerCheck', e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="Klinik PM düzeltmeleri..." />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Düzeltme Nedeni</Form.Label>
                                  <Form.Control as="textarea" rows={2} className={styles.formControl}
                                    value={formData.visitEvaluation?.correctionReason || ''}
                                    onChange={(e) => handleEvalChange('correctionReason', e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="Düzeltme nedeni..." />
                                </div>
                              </Col>
                            </Row>
                          </div>
                          )}

                          {/* ── Section I: Kaynak Tarihleri (Source Dates) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionSource}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              📄 I — Kaynak Tarihleri
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>İlk Kaynak Alınma Tarihi</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.dateInitialSourceReceived || ''}
                                    onChange={(e) => handleEvalChange('dateInitialSourceReceived', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>CPM&apos;den Son Revizyon Talebi</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.dateLatestSourceRevision || ''}
                                    onChange={(e) => handleEvalChange('dateLatestSourceRevision', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Kaynak Kesinleşme Tarihi</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.dateSourceFinalized || ''}
                                    onChange={(e) => handleEvalChange('dateSourceFinalized', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── Section J: Sonuç (Outcome) ── */}
                          <div className={`${styles.homeEvalSection} ${styles.homeSectionSource}`}>
                            <h6 className={styles.homeEvalSectionTitle}>
                              ✅ J — Sonuç
                            </h6>
                            <Row>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>AE/SAE Alındı mı?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.wasAeSaeTaken || ''}
                                    onChange={(e) => handleEvalChange('wasAeSaeTaken', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>DCF Oluşturuldu mu?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.dcfCreated || ''}
                                    onChange={(e) => handleEvalChange('dcfCreated', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* ═══════════════════════════════════════════════════
                              HASTANE (SITE) VISIT — Evaluation Layout
                             ═══════════════════════════════════════════════════ */}

                          {/* ── A — Visit Özet Bilgileri (Teal) ─────────── */}
                          <div className={styles.homeEvalSection} style={{ background: '#E2EFDA', borderColor: '#A9D18E' }}>
                            <h6 className={styles.homeEvalSectionTitle} style={{ color: '#375623' }}>
                              A — Visit Özet Bilgileri
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Proje Adı</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={projectTitle || formData.projectName || '-'}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Merkez ID</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.centerId || formData.centerName || '-'}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Visit Tipi</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.visitType === 'hastane' ? 'Hastane (Site)' : formData.visitType || '-'}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Site Coordinator</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.employeeName || '-'}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Patient ID</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={normalizePatientId(formData.visitEvaluation?.patientId) || normalizePatientId(formData.patientId)}
                                    onChange={(e) => handleEvalChange('patientId', e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="Hasta ID giriniz..." />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Visit Tarihi</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.date || '-'}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Visit Başlangıç Saati</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={formData.startTime || '-'}
                                    disabled readOnly />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── B — Hasta Durumu (Green) ──────────────── */}
                          <div className={styles.homeEvalSection} style={{ background: '#f0fff4', borderColor: '#9ae6b4' }}>
                            <h6 className={styles.homeEvalSectionTitle} style={{ color: '#276749' }}>
                              B — Hasta Durumu
                            </h6>
                            <Row>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Screen Fail?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.screenFail || ''}
                                    onChange={(e) => handleEvalChange('screenFail', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Drop Patient?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.dropPatient || ''}
                                    onChange={(e) => handleEvalChange('dropPatient', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── C — Protokol & CAPA (Yellow) ─────────── */}
                          <div className={styles.homeEvalSection} style={{ background: '#FFF2CC', borderColor: '#FFD966' }}>
                            <h6 className={styles.homeEvalSectionTitle} style={{ color: '#7B6000' }}>
                              C — Protokol & CAPA
                            </h6>
                            <Row>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Protocol Deviation (Protokol Sapması)?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.protocolDeviation || ''}
                                    onChange={(e) => handleEvalChange('protocolDeviation', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              {formData.visitEvaluation?.protocolDeviation === 'yes' && (
                                <Col md={6}>
                                  <div className={styles.formGroup}>
                                    <Form.Label className={styles.formLabel}>CAPA Açıldı mı?</Form.Label>
                                    <Form.Select className={styles.formSelect}
                                      value={formData.visitEvaluation?.capaOpened || ''}
                                      onChange={(e) => handleEvalChange('capaOpened', e.target.value)}
                                      disabled={isReadOnly}>
                                      <option value="">Seçiniz</option>
                                      <option value="yes">Evet</option>
                                      <option value="no">Hayır</option>
                                    </Form.Select>
                                  </div>
                                </Col>
                              )}
                              <Col md={12}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Comments (Yorumlar)</Form.Label>
                                  <Form.Control as="textarea" rows={3} className={styles.formControl}
                                    value={formData.visitEvaluation?.comments || ''}
                                    onChange={(e) => handleEvalChange('comments', e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="Vizite ilişkin notlar ve yorumlar..." />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── D — Tamamlanma Tarihleri (Blue) ──────── */}
                          <div className={styles.homeEvalSection} style={{ background: '#BDD7EE', borderColor: '#8DB4E2' }}>
                            <h6 className={styles.homeEvalSectionTitle} style={{ color: '#1F497D' }}>
                              D — Tamamlanma Tarihleri
                            </h6>
                            <p className={styles.sectionOptionalHint}>
                              Bu bölüm ziyareti tamamlamayı engellemez. Girilmeyen
                              tarihler ziyarette "eksik" olarak işaretlenir ve
                              sonradan girdiğinizde uyarı kalkar.
                            </p>
                            <Row>
                              <Col md={2}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>eCRF Completion Date</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.ecrfCompletionDate || ''}
                                    onChange={(e) => handleEvalChange('ecrfCompletionDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>eCRF Completion Time</Form.Label>
                                  <Form.Control type="time" className={styles.formControl}
                                    value={formData.visitEvaluation?.ecrfCompletionTime || ''}
                                    onChange={(e) => handleEvalChange('ecrfCompletionTime', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Source Doc Completion Date</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.sourceDocCompletionDate || ''}
                                    onChange={(e) => handleEvalChange('sourceDocCompletionDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Source Doc Completion Time</Form.Label>
                                  <Form.Control type="time" className={styles.formControl}
                                    value={formData.visitEvaluation?.sourceDocCompletionTime || ''}
                                    onChange={(e) => handleEvalChange('sourceDocCompletionTime', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Signature Completion Date</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.signatureCompletionDate || ''}
                                    onChange={(e) => handleEvalChange('signatureCompletionDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Signature Completion Time</Form.Label>
                                  <Form.Control type="time" className={styles.formControl}
                                    value={formData.visitEvaluation?.signatureCompletionTime || ''}
                                    onChange={(e) => handleEvalChange('signatureCompletionTime', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                            </Row>
                            
                            {/* Second row for completion status dropdowns */}
                            <Row style={{ marginTop: '1rem' }}>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Source Document Completed</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.sourceDocCompleted || ''}
                                    onChange={(e) => handleEvalChange('sourceDocCompleted', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              {/* Conditional Source Document Completion Time field */}
                              {formData.visitEvaluation?.sourceDocCompleted === 'yes' && (
                                <Col md={4}>
                                  <div className={styles.formGroup}>
                                    <Form.Label className={styles.formLabel}>Kaynak Dok. Tamamlanma Saati</Form.Label>
                                    <Form.Control type="time" className={styles.formControl}
                                      value={formData.visitEvaluation?.sourceDocCompletionTime || ''}
                                      onChange={(e) => handleEvalChange('sourceDocCompletionTime', e.target.value)}
                                      disabled={isReadOnly} />
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </div>

                          {/* ── E — Süreç & SLA (Purple) ─────────────── */}
                          <div className={styles.homeEvalSection} style={{ background: '#D9D2E9', borderColor: '#B4A7D6' }}>
                            <h6 className={styles.homeEvalSectionTitle} style={{ color: '#351C75' }}>
                              E — Süreç & SLA
                            </h6>
                            <Row>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Process End Date</Form.Label>
                                  <Form.Control type="date" className={styles.formControl}
                                    value={formData.visitEvaluation?.processEndDate || ''}
                                    onChange={(e) => handleEvalChange('processEndDate', e.target.value)}
                                    disabled={isReadOnly} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Cycle Time (gün)</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={cycleTime !== null ? `${cycleTime} gün` : '-'}
                                    disabled readOnly
                                    style={{ fontWeight: 700, color: cycleTime !== null ? '#351C75' : '#a0aec0' }} />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>SLA Target (gün)</Form.Label>
                                  <Form.Control type="number" min="0" className={styles.formControl}
                                    value={formData.visitEvaluation?.slaTarget || ''}
                                    onChange={(e) => handleEvalChange('slaTarget', e.target.value)}
                                    disabled={isReadOnly} placeholder="Hedef gün sayısı" />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>SLA Karşılandı mı?</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={slaMet === null ? '-' : slaMet ? 'Evet' : 'Hayır'}
                                    style={{ color: slaMet === true ? '#276749' : slaMet === false ? '#c53030' : undefined, fontWeight: 700 }}
                                    disabled readOnly />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>SLA Aşıldı mı?</Form.Label>
                                  <Form.Control type="text" className={styles.formControl}
                                    value={slaOverdue === null ? '-' : slaOverdue ? 'Evet' : 'Hayır'}
                                    style={{ color: slaOverdue === true ? '#c53030' : slaOverdue === false ? '#276749' : undefined, fontWeight: 700 }}
                                    disabled readOnly />
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* ── F — Sorun Takibi (Red) ───────────────── */}
                          <div className={styles.homeEvalSection} style={{ background: '#FFF5F5', borderColor: '#FEB2B2' }}>
                            <h6 className={styles.homeEvalSectionTitle} style={{ color: '#c53030' }}>
                              F — Sorun Takibi
                            </h6>
                            <Row>
                              <Col md={6}>
                                <div className={styles.formGroup}>
                                  <Form.Label className={styles.formLabel}>Issue Occurred (Sorun Oluştu mu)?</Form.Label>
                                  <Form.Select className={styles.formSelect}
                                    value={formData.visitEvaluation?.issueOccurred || ''}
                                    onChange={(e) => handleEvalChange('issueOccurred', e.target.value)}
                                    disabled={isReadOnly}>
                                    <option value="">Seçiniz</option>
                                    <option value="yes">Evet</option>
                                    <option value="no">Hayır</option>
                                  </Form.Select>
                                </div>
                              </Col>
                              {formData.visitEvaluation?.issueOccurred === 'yes' && (
                                <>
                                  <Col md={6}>
                                    <div className={styles.formGroup}>
                                      <Form.Label className={styles.formLabel}>Sorun Kategorisi</Form.Label>
                                      <Form.Select className={styles.formSelect}
                                        value={formData.visitEvaluation?.issueCategory || ''}
                                        onChange={(e) => handleEvalChange('issueCategory', e.target.value)}
                                        disabled={isReadOnly}>
                                        <option value="">Seçiniz</option>
                                        <option value="iv_access">IV Erişim</option>
                                        <option value="patient_compliance">Hasta Uyumu</option>
                                        <option value="documentation">Dokümantasyon</option>
                                        <option value="logistics">Lojistik</option>
                                        <option value="protocol">Protokol</option>
                                        <option value="equipment">Ekipman</option>
                                        <option value="other">Diğer</option>
                                      </Form.Select>
                                    </div>
                                  </Col>
                                  <Col md={12}>
                                    <div className={styles.formGroup}>
                                      <Form.Label className={styles.formLabel}>Sorun Detayları</Form.Label>
                                      <Form.Control as="textarea" rows={3} className={styles.formControl}
                                        value={formData.visitEvaluation?.issueDetails || ''}
                                        onChange={(e) => handleEvalChange('issueDetails', e.target.value)}
                                        disabled={isReadOnly}
                                        placeholder="Sorunun detayları..." />
                                    </div>
                                  </Col>
                                </>
                              )}
                            </Row>
                          </div>
                        </>
                      )}
                    </Form>
                  );
                })()}
              </div>
            )}

            {SHOW_CRA_APPROVAL_TAB && activeTab === 'craApproval' && (
              <div className={styles.craApproval}>
                <h4>CRA Onay</h4>
                <p>
                  Durum: {craApprovalSent ? 'Gönderildi' : 'Gönderilmedi'}
                  {craApprovalSentAt && (
                    <span style={{ marginLeft: 8, color: '#718096' }}>
                      ({new Date(craApprovalSentAt).toLocaleString('tr-TR')})
                    </span>
                  )}
                </p>
                
                {/* Show expense warning if there are expenses */}
                {expenses.length > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <span style={{ color: '#856404', fontSize: '0.9rem', fontWeight: '500' }}>
                      Bu ziyarette {expenses.length} adet harcama bulunmaktadır. CRA seçimi zorunludur.
                    </span>
                  </div>
                )}
                
                <Form.Select
                  className={styles.formSelect}
                  value={formData.selectedCra}
                  onChange={(e) =>
                    handleInputChange('selectedCra', e.target.value)
                  }
                  disabled={isReadOnly}
                  style={expenses.length > 0 && !formData.selectedCra ? {
                    borderColor: '#ffc107',
                    borderWidth: '2px'
                  } : {}}
                >
                  <option value="">
                    {expenses.length > 0 ? 'CRA Seçiniz (Zorunlu)' : 'Lütfen Seçiniz'}
                  </option>
                  {cras.map((cra) => (
                    <option key={cra.id} value={cra.id}>
                      {cra.name}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  className={`${styles.btnPrimary} mt-2`}
                  onClick={handleSendCraApproval}
                  disabled
                >
                  {isSendingCraApproval
                    ? 'Gönderiliyor...'
                    : craApprovalSent
                    ? 'Tekrar Gönder'
                    : 'Gönder'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.customModalFooter}>
          <Button className={styles.btnSecondary} onClick={onHide}>
            Kapat
          </Button>
          {(isRestrictedUser || isVisitOwner || isAdmin) && !visit?.status && (
            <Button
              className={styles.btnDanger}
              onClick={handleDeleteVisit}
              disabled={isDeleting}
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              {isDeleting ? 'Siliniyor...' : 'Ziyareti Sil'}
            </Button>
          )}
          {/* Admin-only: delete this visit and every later visit of the same
              patient for the same employee. Opens a preview in the parent. */}
          {isAdmin && onCascadeDelete && normalizePatientId(visit?.patientId) && (
            <Button
              className={styles.btnDanger}
              onClick={() => {
                onHide();
                onCascadeDelete(visit);
              }}
              disabled={isDeleting}
              style={{ backgroundColor: '#a71d2a', borderColor: '#a71d2a' }}
            >
              Bu ve Sonraki Ziyaretleri Sil
            </Button>
          )}
          {!isRestrictedUser && (
            <Button
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={saving || isReadOnly}
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          )}
        </div>
      </Modal>
    </StyledModalContainer>
  );
};

// Stock Add Modal Component - Modern Design
const StockAddModal = ({ show, onHide, availableStocks, onStockAdded }) => {
  const [selectedStock, setSelectedStock] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!selectedStock) newErrors.selectedStock = 'Lütfen bir stok seçin';
    if (!quantity || parseFloat(quantity) <= 0) newErrors.quantity = 'Geçerli bir miktar girin';
    
    // Check if quantity exceeds available stock
    const stock = availableStocks.find(s => s.id === selectedStock);
    if (stock && parseFloat(quantity) > parseFloat(stock['Kalan'])) {
      newErrors.quantity = `Maksimum ${stock['Kalan']} adet kullanılabilir`;
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Get stock details
      const stockData = availableStocks.find(s => s.id === selectedStock);
      
      if (!stockData) {
        throw new Error('Seçilen stok bulunamadı');
      }

      console.log('Selected stock data:', stockData);
      
      // Prepare data - ensure all fields are defined
      const data = {
        stockId: selectedStock || '',
        stockName: stockData['Adı'] || stockData.Adı || '',
        stockType: stockData['Stok Tipi'] || stockData.StokTipi || '',
        quantity: parseFloat(quantity) || 0,
        reason: (reason || '').trim()
      };

      console.log('Prepared data to send:', data);

      await onStockAdded(data);
      
      // Reset form only after successful addition
      setSelectedStock('');
      setQuantity('');
      setReason('');
      setErrors({});
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setErrors({ submit: 'Malzeme eklenirken bir hata oluştu' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStockChange = (stockId) => {
    setSelectedStock(stockId);
    setErrors(prev => ({ ...prev, selectedStock: null }));
  };

  const handleQuantityChange = (value) => {
    setQuantity(value);
    setErrors(prev => ({ ...prev, quantity: null }));
  };

  const selectedStockData = availableStocks.find(s => s.id === selectedStock);

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1050,
      padding: '1rem'
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      animation: 'modalSlideIn 0.3s ease-out'
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '1.5rem 2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: 'none'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    icon: {
      width: '48px',
      height: '48px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem'
    },
    title: {
      color: '#ffffff',
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: 0
    },
    subtitle: {
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '0.875rem',
      margin: 0
    },
    closeButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#ffffff',
      fontSize: '1.25rem',
      transition: 'all 0.2s'
    },
    body: {
      padding: '2rem',
      maxHeight: 'calc(90vh - 200px)',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#2d3748',
      fontSize: '0.95rem',
      fontWeight: '600'
    },
    required: {
      color: '#e53e3e',
      marginLeft: '0.25rem'
    },
    infoCard: {
      padding: '1.25rem',
      backgroundColor: '#f0f9ff',
      border: '2px solid #bae6fd',
      borderRadius: '12px',
      marginBottom: '1.5rem'
    },
    infoTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#0c4a6e',
      marginBottom: '0.75rem'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.75rem'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    infoLabel: {
      fontSize: '0.8rem',
      color: '#475569',
      fontWeight: '500'
    },
    infoValue: {
      fontSize: '0.95rem',
      color: '#0f172a',
      fontWeight: '600'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      transition: 'all 0.2s',
      outline: 'none'
    },
    inputError: {
      borderColor: '#fc8181'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.95rem',
      transition: 'all 0.2s',
      outline: 'none',
      resize: 'vertical',
      minHeight: '100px',
      fontFamily: 'inherit'
    },
    errorText: {
      color: '#e53e3e',
      fontSize: '0.85rem',
      marginTop: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
      backgroundColor: '#f8fafc'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    buttonCancel: {
      backgroundColor: '#e2e8f0',
      color: '#4a5568'
    },
    buttonSubmit: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#ffffff',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
    }
  };

  if (!show) return null;

  return (
    <div style={modalStyles.overlay} onClick={onHide}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <div style={modalStyles.headerContent}>
            <div style={modalStyles.icon}>
              📦
            </div>
            <div>
              <h2 style={modalStyles.title}>Malzeme Ekle</h2>
              <p style={modalStyles.subtitle}>Ziyaret için kullanılacak malzemeyi seçin</p>
            </div>
          </div>
          <button 
            style={modalStyles.closeButton}
            onClick={onHide}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={modalStyles.body}>
          <form onSubmit={handleSubmit}>
            {/* Stock Selection */}
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>
                Stok Seçin
                <span style={modalStyles.required}>*</span>
              </label>
              
              {/* Info message about assigned stocks */}
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                marginBottom: '0.75rem',
                fontSize: '0.875rem',
                color: '#0c4a6e'
              }}>
                ℹ️ Sadece size atanan stoklar gösterilmektedir.
              </div>
              
              <select
                value={selectedStock}
                onChange={(e) => handleStockChange(e.target.value)}
                style={{
                  ...modalStyles.input,
                  ...(errors.selectedStock ? modalStyles.inputError : {})
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = errors.selectedStock ? '#fc8181' : '#e2e8f0'}
              >
                <option value="">Stok seçiniz...</option>
                {availableStocks.length === 0 ? (
                  <option disabled>Size atanmış stok bulunamadı</option>
                ) : (
                  availableStocks.map(stock => {
                    const remaining = parseFloat(stock['Kalan']) || 0;
                    return (
                      <option key={stock.id} value={stock.id}>
                        {stock['Adı']} - {stock['Stok Tipi']} (Kalan: {remaining} adet)
                      </option>
                    );
                  })
                )}
              </select>
              
              {errors.selectedStock && (
                <div style={modalStyles.errorText}>
                  ⚠️ {errors.selectedStock}
                </div>
              )}
            </div>

            {/* Selected Stock Info */}
            {selectedStockData && (
              <div style={modalStyles.infoCard}>
                <div style={modalStyles.infoTitle}>
                  ℹ️ Seçilen Stok Bilgileri
                </div>
                <div style={modalStyles.infoGrid}>
                  <div style={modalStyles.infoItem}>
                    <span style={modalStyles.infoLabel}>Stok Adı</span>
                    <span style={modalStyles.infoValue}>{selectedStockData['Adı']}</span>
                  </div>
                  <div style={modalStyles.infoItem}>
                    <span style={modalStyles.infoLabel}>Stok Tipi</span>
                    <span style={modalStyles.infoValue}>{selectedStockData['Stok Tipi']}</span>
                  </div>
                  <div style={modalStyles.infoItem}>
                    <span style={modalStyles.infoLabel}>Mevcut Miktar</span>
                    <span style={{
                      ...modalStyles.infoValue,
                      color: parseFloat(selectedStockData['Kalan']) < 10 ? '#dc2626' : '#059669'
                    }}>
                      {selectedStockData['Kalan']} adet
                    </span>
                  </div>
                  <div style={modalStyles.infoItem}>
                    <span style={modalStyles.infoLabel}>Durum</span>
                    <span style={modalStyles.infoValue}>
                      {selectedStockData['Durum'] === 'aktif' ? '✅ Aktif' : '❌ İnaktif'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Quantity Input */}
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>
                Kullanım Miktarı
                <span style={modalStyles.required}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                placeholder="Kullanılacak miktar"
                style={{
                  ...modalStyles.input,
                  ...(errors.quantity ? modalStyles.inputError : {})
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = errors.quantity ? '#fc8181' : '#e2e8f0'}
              />
              {errors.quantity && (
                <div style={modalStyles.errorText}>
                  ⚠️ {errors.quantity}
                </div>
              )}
            </div>

            {/* Reason Textarea */}
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>
                Kullanım Nedeni
                <span style={{ color: '#718096', fontWeight: '400', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                  (Opsiyonel)
                </span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Malzemenin kullanım nedeni..."
                style={modalStyles.textarea}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div style={modalStyles.errorText}>
                ⚠️ {errors.submit}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div style={modalStyles.footer}>
          <button
            type="button"
            style={{ ...modalStyles.button, ...modalStyles.buttonCancel }}
            onClick={onHide}
            disabled={isSubmitting}
            onMouseEnter={(e) => !isSubmitting && (e.target.style.backgroundColor = '#cbd5e0')}
            onMouseLeave={(e) => !isSubmitting && (e.target.style.backgroundColor = '#e2e8f0')}
          >
            ✕ İptal
          </button>
          <button
            type="submit"
            style={{ 
              ...modalStyles.button, 
              ...modalStyles.buttonSubmit,
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
            onClick={handleSubmit}
            disabled={isSubmitting}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }
            }}
          >
            {isSubmitting ? '⏳ Ekleniyor...' : '✓ Ekle'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitModal;
